import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { CacheService } from '../cache-service/cache.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, retry, shareReplay, tap, timeout } from 'rxjs/operators';
import { CACHE } from '../../utils';
import { AuditModel, GroupModel, IncidentAuditModel, IncidentModel, IncidentUrgency, OTPModel, PatientModel, SMSModel, SurveyModel, UserModel, UserRole } from './models';


@Injectable({
  providedIn: 'root'
})

export class ApiService {

  private readonly REQUEST_TIMEOUT = 10000; // 10 seconds
  private readonly MAX_RETRIES = 2;
  private readonly base_url = environment.base_url;
  private readonly sms = "https://2gbmav9xj7.execute-api.af-south-1.amazonaws.com/prod/sms";
  uploadLink = `${this.base_url}api/Upload/Document`;


  constructor(private http: HttpClient, private cacheService: CacheService) { }


  private readonly headers = new HttpHeaders({
    'Content-Type': 'application/json',
    'responseType': 'json',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, PATCH, OPTIONS',
    'Access-Control-Allow-Headers': 'X-Requested-With, content-type, Authorization',
    'Authorization': 'App 991a1a5dc59cd189a5d1696d356f7cc8-9371eeed-1bf9-42e8-a8a2-1fd7ce368ab0',
    'x-Gateway-APIKey': 'ccccf874-1965-49c7-be6c-c0c0fbb8bf4b'
  });

  private readonly corsHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    'responseType': 'json',
    'Accept': '*/*',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET, POST, PUT, PATCH',
    'Access-Control-Allow-Headers': '*'
  });

  // URL



  generateUniqueId(): string {
    let id = new Date();
    return id.valueOf().toString();
  }

  getSurveyTotal(): number {
    return 23;
  }

  getERSurveyTotal(): number {
    return 17;
  }


  getHODs(): string[] {
    let id: string[] = [
      "gerrit.vandenberg@phelangbonolo.co.za",
      "evelyn.matamba@phelangbonolo.co.za",
      "marisa.marais@phelangbonolo.co.za",
      "tumi.mphake@phelangbonolo.co.za"
    ];

    return id;
  }



  ////////////// NOTIFICATION service ////////////////////////
  sendMail(data: any): Observable<any> {
    return this.http.post(`${this.base_url}sendMail`, data);
  }

  sendSMS(data: SMSModel): Observable<any> {
    return this.http.post(this.sms, data);
  }


  // Private helper methods
  private getWithCache(url: string, cacheKey: string): Observable<any> {
    if (this.cacheService.has(cacheKey)) {
      return of(this.cacheService.get(cacheKey));
    }

    return this.http.get(url, { headers: this.headers }).pipe(
      timeout(this.REQUEST_TIMEOUT),
      retry(this.MAX_RETRIES),
      tap(data => this.cacheService.set(cacheKey, data)),
      catchError(error => {
        console.error(`Request failed for ${url}:`, error);
        const cachedData = this.cacheService.get(cacheKey);
        return cachedData ? of(cachedData) : throwError(() => error);
      }),
      shareReplay(1)
    );
  }

  private invalidateCache(cacheKey: string): void {
    this.cacheService.remove(cacheKey);
  }


  /////////////////////////////// OTP /////////////////////////////////

  saveOTP(data: OTPModel): Observable<any> {
    return this.http.put<any>(`${this.base_url}otp`, data, { headers: this.corsHeaders });
  }

  getAllOTP(): Observable<OTPModel[]> {
    return this.http.get<OTPModel[]>(`${this.base_url}otp`, { headers: this.corsHeaders });
  }

  getOTPById(id: string): Observable<OTPModel> {
    return this.http.get<OTPModel>(`${this.base_url}otp/${id}`);
  }

  deleteOTP(id: string): Observable<any> {
    return this.http.delete<any>(`${this.base_url}otp/${id}`);
  }

  /////////////////////////////// PATIENTS /////////////////////////////////
  savePatient(data: PatientModel): Observable<any> {
    return this.http.put<any>(`${this.base_url}patients`, data, { headers: this.corsHeaders })
      .pipe(tap(() => this.invalidateCache(CACHE.Patients)));
  }


  getAllPatients(): Observable<PatientModel[]> {
    return this.getWithCache(`${this.base_url}patients`, CACHE.Patients);
  }


  getPatientById(id: string): Observable<PatientModel> {
    return this.getWithCache(
      `${this.base_url}patients/${id}`,
      `${CACHE.Patients}:${id}`
    );
  }



  deletePatient(id: string): Observable<any> {
    return this.http.delete<any>(`${this.base_url}patients/${id}`)
      .pipe(tap(() => this.invalidateCache(CACHE.Patients)));
  }

  updateCachedPatient(): void {
    const cacheKey = CACHE.Patients;
    const apiUrl = `${this.base_url}patients`;

    this.http.get<PatientModel>(apiUrl).pipe(
      shareReplay(1),
      timeout(this.REQUEST_TIMEOUT),
      retry(this.MAX_RETRIES),
      tap(data => {
        if (data) {
          this.cacheService.set(cacheKey, data);

        }
      }),
      catchError(error => {
        console.error('Cache ' + cacheKey?.toLocaleUpperCase() + ' update failed:', error);
        return throwError(() => error);
      }),
      finalize(() => console.log(cacheKey?.toLocaleUpperCase() + ' Cache Updated Successfully!'))
    ).subscribe({
      error: (error) => {
        console.error('Failed to update ' + cacheKey?.toLocaleUpperCase() + ' cache:', error);
        // You might want to handle the error here (e.g., show a notification)
      }
    });
  }



  /////////////////////////////// SURVEY /////////////////////////////////
  saveSurvey(data: SurveyModel): Observable<any> {
    return this.http.put<any>(`${this.base_url}survey`, data, { headers: this.corsHeaders })
      .pipe(tap(() => this.invalidateCache(CACHE.Surveys)));
  }

  getAllSurvey(): Observable<SurveyModel[]> {
    return this.getWithCache(`${this.base_url}survey`, CACHE.Surveys);
  }


  getSurveyById(id: string): Observable<SurveyModel> {
    return this.getWithCache(
      `${this.base_url}survey/${id}`,
      `${CACHE.Surveys}:${id}`
    );
  }

  deleteSurvey(id: string): Observable<any> {
    return this.http.delete<any>(`${this.base_url}survey/${id}`)
      .pipe(tap(() => this.invalidateCache(CACHE.Surveys)));
  }



  updateCachedSurvey(): void {
    const cacheKey = CACHE.Surveys;
    const apiUrl = `${this.base_url}survey`;

    this.http.get<SurveyModel[]>(apiUrl).pipe(
      shareReplay(1),
      timeout(this.REQUEST_TIMEOUT),
      retry(this.MAX_RETRIES),
      tap(data => {
        if (data) {
          this.cacheService.set(cacheKey, data);

        }
      }),
      catchError(error => {
        console.error('Cache ' + cacheKey?.toLocaleUpperCase() + ' update failed:', error);
        return throwError(() => error);
      }),
      finalize(() => console.log(cacheKey?.toLocaleUpperCase() + ' Cache Updated Successfully!'))
    ).subscribe({
      error: (error) => {
        console.error('Failed to update ' + cacheKey?.toLocaleUpperCase() + ' cache:', error);
        // You might want to handle the error here (e.g., show a notification)
      }
    });
  }


  /////////////////////////////// USERS /////////////////////////////////
  saveUser(data: UserModel): Observable<any> {
    return this.http.put<any>(`${this.base_url}user`, data, { headers: this.corsHeaders })
      .pipe(tap(() => this.invalidateCache(CACHE.Users)));
  }


  getAllUsers(): Observable<UserModel[]> {
    return this.getWithCache(`${this.base_url}user`, CACHE.Users);
  }


  getUserById(id: string): Observable<UserModel> {
    return this.getWithCache(
      `${this.base_url}user/${id}`,
      `${CACHE.Users}:${id}`
    );
  }

  updateCachedUsers(): void {
    const cacheKey = CACHE.Users;
    const apiUrl = `${this.base_url}user`;

    this.http.get<UserModel[]>(apiUrl).pipe(
      shareReplay(1),
      timeout(this.REQUEST_TIMEOUT),
      retry(this.MAX_RETRIES),
      tap(data => {
        if (data) {
          this.cacheService.set(cacheKey, data);

        }
      }),
      catchError(error => {
        console.error('Cache ' + cacheKey?.toLocaleUpperCase() + ' update failed:', error);
        return throwError(() => error);
      }),
      finalize(() => console.log(cacheKey?.toLocaleUpperCase() + ' Cache Updated Successfully!'))
    ).subscribe({
      error: (error) => {
        console.error('Failed to update ' + cacheKey?.toLocaleUpperCase() + ' cache:', error);
        // You might want to handle the error here (e.g., show a notification)
      }
    });
  }


  /////////////////////////////// INCIDENTS /////////////////////////////////
  saveIncident(data: IncidentModel): Observable<any> {
    return this.http.put<any>(`${this.base_url}incident`, data, { headers: this.corsHeaders })
      .pipe(tap(() => this.invalidateCache(CACHE.Incidents)));
  }

  getAllIncidents(): Observable<IncidentModel[]> {
    return this.getWithCache(`${this.base_url}incident`, CACHE.Incidents);
  }

  getIncidentById(id: string): Observable<IncidentModel> {
    return this.getWithCache(
      `${this.base_url}incident/${id}`,
      `${CACHE.Incidents}:${id}`
    );
  }


  deleteIncident(id: string): Observable<any> {
    return this.http.delete<any>(`${this.base_url}incident/${id}`)
      .pipe(tap(() => this.invalidateCache(CACHE.Incidents)));
  }

  updateCachedIncidents(): void {
    const cacheKey = CACHE.Incidents;
    const apiUrl = `${this.base_url}incident`;

    this.http.get<IncidentModel[]>(apiUrl).pipe(
      shareReplay(1),
      timeout(this.REQUEST_TIMEOUT),
      retry(this.MAX_RETRIES),
      tap(data => {
        if (data) {
          this.cacheService.set(cacheKey, data);

        }
      }),
      catchError(error => {
        console.error('Cache ' + cacheKey?.toLocaleUpperCase() + ' update failed:', error);
        return throwError(() => error);
      }),
      finalize(() => console.log(cacheKey?.toLocaleUpperCase() + ' Cache Updated Successfully!'))
    ).subscribe({
      error: (error) => {
        console.error('Failed to update ' + cacheKey?.toLocaleUpperCase() + ' cache:', error);
        // You might want to handle the error here (e.g., show a notification)
      }
    });
  }


  /////////////////////////////// ASSIGNMENT GROUP /////////////////////////////////
  saveGroup(data: GroupModel): Observable<any> {
    return this.http.put<any>(`${this.base_url}group`, data, { headers: this.corsHeaders })
      .pipe(tap(() => this.invalidateCache(CACHE.Grouups)));
  }


  getAllGroups(): Observable<GroupModel[]> {
    return this.getWithCache(`${this.base_url}group`, CACHE.Grouups);
  }


  getGroupById(id: string): Observable<any> {
    return this.getWithCache(
      `${this.base_url}group/${id}`,
      `${CACHE.Grouups}:${id}`
    );
  }



  deleteGroup(id: string): Observable<any> {
    return this.http.delete<any>(`${this.base_url}group/${id}`)
      .pipe(tap(() => this.invalidateCache(CACHE.Grouups)));
  }


  updateCachedGroups(): void {
    const cacheKey = CACHE.Grouups;
    const apiUrl = `${this.base_url}group`;

    this.http.get<GroupModel[]>(apiUrl).pipe(
      shareReplay(1),
      timeout(this.REQUEST_TIMEOUT),
      retry(this.MAX_RETRIES),
      tap(data => {
        if (data) {
          this.cacheService.set(cacheKey, data);

        }
      }),
      catchError(error => {
        console.error('Cache ' + cacheKey?.toLocaleUpperCase() + ' update failed:', error);
        return throwError(() => error);
      }),
      finalize(() => console.log(cacheKey?.toLocaleUpperCase() + ' Cache Updated Successfully!'))
    ).subscribe({
      error: (error) => {
        console.error('Failed to update ' + cacheKey?.toLocaleUpperCase() + ' cache:', error);
        // You might want to handle the error here (e.g., show a notification)
      }
    });
  }


  /////////////////////////////// AUDIT LOG /////////////////////////////////
  saveAudit(data: AuditModel): Observable<any> {
    return this.http.put<any>(this.base_url + "audit", data, { headers: this.corsHeaders })
  }


  getAllAudit(): Observable<AuditModel[]> {
    return this.http.get<AuditModel[]>(this.base_url + "audit", { headers: this.corsHeaders });
  }


  getAuditById(id: string): Observable<AuditModel> {
    return this.http.get<AuditModel>(this.base_url + "audit/" + id);
  }


  generateAuditId(): string {
    let id = new Date();
    return id.valueOf().toString();
  }



  /////////////////////////////// INCIDENT AUDIT /////////////////////////////////
  saveIcidentAudit(data: IncidentAuditModel): Observable<any> {
    return this.http.put<any>(`${this.base_url}incidentAudit`, data, { headers: this.corsHeaders })
      .pipe(tap(() => this.invalidateCache(CACHE.IncidentAudit)));
  }


  getAllIncidentAudit(): Observable<IncidentAuditModel[]> {
    return this.getWithCache(`${this.base_url}incidentAudit`, CACHE.Incidents);
  }


  getIncidentAuditById(id: string): Observable<IncidentAuditModel> {
    return this.getWithCache(
      `${this.base_url}incidentAudit/${id}`,
      `${CACHE.IncidentAudit}:${id}`
    );
  }


  generateIncidentAuditId(): string {
    let id = new Date();
    return id.valueOf().toString();
  }


  deleteIncidentAudit(id: string): Observable<any> {
    return this.http.delete<any>(`${this.base_url}incidentAudit/${id}`)
      .pipe(tap(() => this.invalidateCache(CACHE.IncidentAudit)));
  }


  updateCachedIncidentAudit(): void {
    const cacheKey = CACHE.IncidentAudit;
    const apiUrl = `${this.base_url}incidentAudit`;

    this.http.get<IncidentAuditModel[]>(apiUrl).pipe(
      shareReplay(1),
      timeout(this.REQUEST_TIMEOUT),
      retry(this.MAX_RETRIES),
      tap(data => {
        if (data) {
          this.cacheService.set(cacheKey, data);

        }
      }),
      catchError(error => {
        console.error('Cache ' + cacheKey?.toLocaleUpperCase() + ' update failed:', error);
        return throwError(() => error);
      }),
      finalize(() => console.log(cacheKey?.toLocaleUpperCase() + ' Cache Updated Successfully!'))
    ).subscribe({
      error: (error) => {
        console.error('Failed to update ' + cacheKey?.toLocaleUpperCase() + ' cache:', error);
        // You might want to handle the error here (e.g., show a notification)
      }
    });
  }

  refeshCache(): void {
    this.updateCachedIncidentAudit();
    this.updateCachedIncidents();
    this.updateCachedPatient();
    this.updateCachedGroups();
    this.updateCachedSurvey();
    this.updateCachedUsers();
  }

  /////////////////////////////// DROPDOWN VALUES /////////////////////////////////
  getDropdownUrgecy(): IncidentUrgency[] {

    let data: IncidentUrgency[] = [
      "Critical",
      "High",
      "Medium",
      "Low"
    ]

    return data;
  }


  getDropdownAssignmentGroup(): string[] {

    let data: string[] = [
      "UNIT MANAGERS",
      "INFECTION PREVENTION",
      "OHS",
      "ADMIN/FINANCE",
      "CATERING",
      "CLEANING AND WASTE",
      "SECURITY",
      "PHARMACY",
      "MANAGERS",
      "RADIOLOGY",
      "LABORATORY SERVICES",
      "CLIENT SERVICES",
      "HR",
      "LAUNDRY SERVICES",
      "TECHNICAL AND INFRASTRUCTURE",
      "IT",
    ]

    data.sort((a, b) => a.localeCompare(b))

    return data;
  }


  getDropdownService(): string[] {

    let data: string[] = [
      "LEGAL CHARGES",
      "INFECTION",
      "FINANCIAL LOSS",
      "DEATH",
      "UNHAPPY STAFF",
      "UNHAPPY PATIENT",
      "UNHAPPY VISITOR",
      "DOCTORS COMPLAINT",
      "PATIENT COMPLAINT",
      "BURNS",
      "QUALITY ISSUES",
      "INJURY",
      "DAMAGE",
      "THEFT",
      "STAFF INJURY",
      "POOR CLINICAL OUTCOMES",
      "POOR QUALITY OUTCOMES",
      "POOR CLINICAL GOVERNANCE",
      "SECURITY",
      "EQUIPMENT"
    ]
    data.sort((a, b) => a.localeCompare(b))

    return data;
  }


  getDropdownCategory(): string[] {

    let data: string[] = [
      "TRAINING",
      "BCA BUNDLES",
      "SECURITY MEASURES",
      "APPROVED SUPPLIERS",
      "DOCTOR'S PROTOCOLS",
      "QUALITY CONTROL(QIP)",
      "DOCUMENTATION AUDITS",
      "REMEDIAL ACTION(HR)",
      "IPC MEASURES",
      "POLICIES AND PROCEDURES",
      "BQC REPORTING"
    ]
    data.sort((a, b) => a.localeCompare(b))

    return data;
  }



  getDropdownWard(): string[] {

    let data: string[] = [
      "Surgical A",
      "Surgical B",
      "General Ward",
      "ICU",
      "Maternity",
      "Paeds",
      "NICU",
      "Emergency Unit (ER)",
      "Pharmacy"
    ]
    data.sort((a, b) => a.localeCompare(b))

    return data;
  }



  getDropdownDepartmentWard(): string[] {

    let data: string[] = [
      "Surgical A",
      "Surgical B",
      "General Ward",
      "ICU",
      "Maternity",
      "Paeds",
      "NICU",
      "Emergency Unit (ER)",
      "Pharmacy",
      "Marketing & Client Services",
      "Nursing Services Manager",
      "Hospital General Manager",
      "Night Supervisor",
      "IPC",
      "Surgical & High Care",
      "Casualty",
      "Theatre",
      "Reception",
      "Cleaning",
      "Catering"
    ]
    data.sort((a, b) => a.localeCompare(b))

    return data;
  }



  getDropdownLocation(): string[] {

    let data: string[] = [
      "CATERING",
      "CSSD",
      "FINANCE",
      "RECEPTION",
      "ADMIN",
      "OHS",
      "IP",
      "IT",
      "GENERAL WARD",
      "PAEDIATRIC WARD",
      "MATERNITY WARD",
      "NNICU",
      "ICU",
      "HIGHCARE",
      "SURGICAL WARD",
      "THEATRE",
      "ER",
      "LAUNDRY",
      "PHARMACY",
      "TECHNICAL",
      "CLEANING SERVICES",
      "X-RAYS",
      "AMPATH",
      "MEDICAL SUITES",
      "MARKETING",
      "HR",
      "TRAINING DEPARTMENT"
    ]
    data.sort((a, b) => a.localeCompare(b))

    return data;
  }

  getDropdownIncidentType(): string[] {

    let data: string[] = [
      "ANATOMICAL WASTE",
      "ARMED ROBBERY",
      "BOMB THREAT",
      "BLOOD AND BLOOD PRODUCTS",
      "BUSINESS INTERRUPTION",
      "CHEMICALS",
      "CAUTI",
      "CLABSI",
      "INFECTION",
      "CLINICAL OUTCOMES",
      "CONDUCT AND ETHICAL ISSUES",
      "CONTRACTORS",
      "DOCUMENTATION",
      "DVT",
      "ELECTRICITY",
      "EQUIPMENT",
      "FACILITY",
      "FALLS",
      "FINANCIAL",
      "FIRE",
      "FLOORS",
      "FOOD",
      "HAI",
      "HOSPITAL VEHICLES",
      "IDENTIFICATION ERROR",
      "INFRASTRUCTURE",
      "IT SYSTEMS",
      "LINEN",
      "MEDICAL GASES",
      "MEDICAL WATSE",
      "MEDICATION ERROR",
      "OPERATING ROOM",
      "OXYGEN",
      "PARKING",
      "PATIENT",
      "POWER FAILURE",
      "POPPI VIOLATION",
      "PRESSURE ULCERS",
      "RADIATION",
      "SHARPS",
      "SKIN ISSUES",
      "SSI",
      "STAFFING",
      "STAFF MISCONDUCT",
      "STOCK",
      "STORAGE AREAS",
      "SUPPLIERS",
      "SURGERY",
    ]
    data.sort((a, b) => a.localeCompare(b))

    return data;
  }


  getDropdownUserRoles(): UserRole[] {

    let data: UserRole[] = [
      "Admin",
      "User",
      "Superuser"
    ]
    data.sort((a, b) => a.localeCompare(b))

    return data;
  }


  getDropdownImpacted(): string[] {

    let data: string[] = [
      "Single Personnel",
      "Multiple Personnel"
    ]
    data.sort((a, b) => a.localeCompare(b))

    return data;
  }

  getAllSurveyJSON() {
    return of([
      {
        "cellphonenumber": "0727282924",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "mashimbye.advo@gmail.com",
        "surname": "Mashimbye",
        "dateCaptured": "2024-09-02T10:17:39.585Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2402-12174",
        "name": "Mfumo",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0769057472",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "May God increase the nurses territory and meet all their need. I'm grateful for your stuff."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Excellent service"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Amazing a life saver."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Tried their best."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "An area I'm not satisfied with. Add food children love. A pie, chips here and there. Interesting desert a milk tart her and there. Please work on this area"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Thank you. Make your meals interesting please"
          }
        ],
        "overallScore": 93,
        "emailAddress": "nomsajivane@gmail.com",
        "surname": "Jivane",
        "dateCaptured": "2024-06-13T12:54:05.962Z",
        "ward": "Paeds",
        "comment": "Yes. Contact me",
        "feedback": "yes",
        "id": "2413-14541",
        "name": "Nomsa",
        "reference": "Search engine (e.g., Google or Bing)"
      },
      {
        "cellphonenumber": "0749703735",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I was really satisfied and wish to congratulate the admin staff on the good work.\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Excellent work "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 87,
        "emailAddress": "salanesono@gmail.com",
        "surname": "Sono",
        "dateCaptured": "2024-06-06T06:58:00.066Z",
        "ward": "General Ward",
        "comment": "Maintenance of the restroom in my room to be checked ",
        "feedback": "yes",
        "id": "2406-08586",
        "name": "Mokgadi Sania",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0797460663",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "chidisamson57@gmail.com",
        "surname": "Chidi",
        "dateCaptured": "2024-09-08T10:03:15.640Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2408-12036",
        "name": "Samson",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0767741544",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Excellent keep it up guys "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Extremely good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Super "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "May good Lord bless you "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Nursing it's calling to them "
          }
        ],
        "overallScore": 90.4,
        "emailAddress": "ayandalote@gmail.com",
        "surname": "Lothe",
        "dateCaptured": "2024-06-26T08:43:18.372Z",
        "ward": "General Ward",
        "comment": "No  thanks ",
        "feedback": "yes",
        "id": "2426-10436",
        "name": "Ayanda",
        "reference": "Facebook"
      },
      {
        "cellphonenumber": "0732515361",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Everything went well and quick."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "They always inform me with the medication and even when they give it to my child the don't just give but they will let me know."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "She always wear a smile and give me enough time to talk to her, she is so patient with us. I really like her because she is not just a doctor but she is a mum."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "They clean well and they are friendly all of the cleaners but both room is not that clean, there is no hand washing soap and the sanitizer as well. They everything is perfect besides that."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "They are well presented, even their food is nice"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "I'm happy with everything in the hospital. I won't mind coming back."
          }
        ],
        "overallScore": 97.4,
        "emailAddress": "slindie22@gmail.com",
        "surname": "Mtshali",
        "dateCaptured": "2024-08-07T08:21:36.387Z",
        "ward": "Paeds",
        "comment": "Beds for moms must at least be comfortable. Can you add another bathroom for moms because when you are very pressed you can't go to toilet if someone is bathing there",
        "feedback": "yes",
        "id": "2407-10218",
        "name": "Slindile",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0798570049",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I wished for a discussion on the type of treatment that was administered to be explained, like taking samples "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 94.8,
        "emailAddress": "margaret.lekukela@health.gov.za",
        "surname": "Lekukela ",
        "dateCaptured": "2024-08-25T09:07:46.762Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2425-11077",
        "name": "Margaret  Lekukela",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0784091602",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Everything was done correctly "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Doctor explain everything to me very well, according to my understanding "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "The ward was dirty "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "No comment "
          }
        ],
        "overallScore": 80.9,
        "emailAddress": "tsholofelosinah5@gmail.com",
        "surname": "Mpete",
        "dateCaptured": "2024-08-01T12:00:12.366Z",
        "ward": "Paeds",
        "comment": "No",
        "feedback": "yes",
        "id": "2401-14undefined2",
        "name": "Tsholofelo ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0814159688",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Keep up the good work "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "khumotr@yahoo.com",
        "surname": "Mashabela",
        "dateCaptured": "2024-09-19T13:27:31.654Z",
        "ward": "General Ward",
        "comment": "Keep up the good work ",
        "feedback": "yes",
        "id": "2419-15271",
        "name": "Noxolo",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0737962309",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Sometimes once a day ."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 81.7,
        "emailAddress": "989 block Gg",
        "surname": "Songo",
        "dateCaptured": "2024-09-08T11:16:11.060Z",
        "ward": "General Ward",
        "feedback": "no",
        "id": "2408-13165",
        "name": "Phinah ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0769217752",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 87.8,
        "emailAddress": "tebogomatladisa@yahoo.com",
        "surname": "Matladisa",
        "dateCaptured": "2024-08-13T11:29:26.103Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2413-13293",
        "name": "Elsie ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0679799038",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Yes"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 97.4,
        "emailAddress": "bella.mabulana@gmail.com",
        "surname": "Mashigo",
        "dateCaptured": "2024-06-03T07:06:52.794Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2403-09069",
        "name": "Bella",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0813231484",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93,
        "emailAddress": "itumelengmasenya@gmail.com",
        "surname": "Masenya",
        "dateCaptured": "2024-08-21T06:49:19.887Z",
        "ward": "Maternity",
        "feedback": "yes",
        "id": "2421-08494",
        "name": "Itumeleng",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0849276493",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "They explained everything clearly "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "She was amazing "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "We had great food.Thank you"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Definitely going to recommend it to those who need help "
          }
        ],
        "overallScore": 96.5,
        "emailAddress": "mandisasibanyoni93@gmail.com",
        "surname": "Sibanyoni",
        "dateCaptured": "2024-08-27T13:48:23.589Z",
        "ward": "Paeds",
        "comment": "Nah",
        "feedback": "yes",
        "id": "2427-15484",
        "name": "Mandisa",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0765779459",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "kemelosimon@gmail.com",
        "surname": "Mahlaela",
        "dateCaptured": "2024-09-02T11:21:02.447Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2402-13212",
        "name": "Simon",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0730631034",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Nurses ,Sisters are very polite and friendly.Doctors are also friendly and kind."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "The food is good ."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 80,
        "emailAddress": "lebzajose@gmail.com",
        "surname": "Moloto",
        "dateCaptured": "2024-08-19T13:26:06.470Z",
        "ward": "General Ward",
        "comment": "",
        "feedback": "no",
        "id": "2419-15266",
        "name": "Lebogang Josephine ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0833794070",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Very g"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Very good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Very good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Very good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good"
          }
        ],
        "overallScore": 100,
        "emailAddress": "",
        "surname": "Mahladisa",
        "dateCaptured": "2024-06-22T13:30:35.537Z",
        "ward": "Surgical B",
        "comment": "Satisfied ",
        "feedback": "yes",
        "id": "2422-15309",
        "name": "Matome",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0724334063",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 86.1,
        "emailAddress": "katekanekhoza9@gmail.com",
        "surname": "Khoza ",
        "dateCaptured": "2024-09-20T09:12:35.641Z",
        "ward": "General Ward",
        "feedback": "no",
        "id": "2420-11121",
        "name": "Katekane ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0768640567",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "None"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "None"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "None"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "None"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "None"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "None"
          }
        ],
        "overallScore": 100,
        "emailAddress": "ndukuhlehle55@gmail.com",
        "surname": "Nduku",
        "dateCaptured": "2024-09-06T11:05:18.609Z",
        "ward": "Surgical B",
        "comment": "None",
        "feedback": "yes",
        "id": "2406-13054",
        "name": "Zinhle",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0787108132",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "sn.mollo12@gmail.com",
        "surname": "Mollo",
        "dateCaptured": "2024-08-21T08:36:06.031Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2421-10364",
        "name": "Agnes",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0825994907",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "100/💯"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Ecellent"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Ellent"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Exellent"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Ecellent"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Exellent"
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "morngwabaloyi5@gmai.com",
        "surname": "Baloyi",
        "dateCaptured": "2024-09-05T08:22:40.636Z",
        "ward": "Surgical B",
        "feedback": "no",
        "id": "2405-10224",
        "name": "Morongwa",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0796576689",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The waiting sitting platforms need some cushioning to support back while waiting for admission.The back is injured by the rest for it's not cushioned.We have some back problems that needed to be taken care not to worsen the condition."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 96.5,
        "emailAddress": "buisile.ncukana@bmw.co.za",
        "surname": "Ncukana",
        "dateCaptured": "2024-06-10T16:17:58.057Z",
        "ward": "Maternity",
        "comment": "Please place some cushions in the waiting area",
        "feedback": "yes",
        "id": "2410-18176",
        "name": "Buisile Jeremiah",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0829311172",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "judymofokeng.mj@gmail.com",
        "surname": "Mokoena",
        "dateCaptured": "2024-07-16T11:58:57.804Z",
        "ward": "Maternity",
        "feedback": "no",
        "id": "2416-13586",
        "name": "Mathapelo",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0676877607",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The staff members were very friendly and ready to assist.Thank you for your care and support."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Regularly "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "The meal was attractive and tasty"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "I would recommend my beneficiaries to this hospital"
          }
        ],
        "overallScore": 94.8,
        "emailAddress": "tsholofeloswantle@gmail.com",
        "surname": "Swantle",
        "dateCaptured": "2024-06-29T14:25:11.915Z",
        "ward": "Maternity",
        "feedback": "yes",
        "id": "2429-16251",
        "name": "Tsholofelo ",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0745022516",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 13.9,
        "emailAddress": "evelyn.matamba@phelangbonolo.co.za",
        "surname": "Matamba",
        "dateCaptured": "2024-06-25T15:43:23.195Z",
        "ward": "Surgical A",
        "feedback": "no",
        "id": "2425-17439",
        "name": "Evelyn",
        "reference": "Facebook"
      },
      {
        "cellphonenumber": "0659342002",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 87,
        "emailAddress": "lehlohonolohleko6@gmail.com",
        "surname": "Hleko",
        "dateCaptured": "2024-08-23T09:45:29.988Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2423-11459",
        "name": "Lehlohonolo ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0739552034",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 91.3,
        "emailAddress": "",
        "surname": "Olifant",
        "dateCaptured": "2024-06-12T11:12:40.320Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2412-13122",
        "name": "Petunia ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0715383063",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Every day 3 times"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 89.6,
        "emailAddress": "marymmating@gmail.com",
        "surname": "Thaba",
        "dateCaptured": "2024-06-17T11:22:57.632Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2417-13224",
        "name": "Mary",
        "reference": "Search engine (e.g., Google or Bing)"
      },
      {
        "cellphonenumber": "0607881411",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I think some of the High care  staff must not look or treat us the TB patients low and disgusting "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Most patients struggle to eat and the menu is tabled,like you can't change it or they will say that is not on the menu but the food is fat and oily"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 94.8,
        "emailAddress": "chicochuck6@gmail.com",
        "surname": "Mpye",
        "dateCaptured": "2024-06-06T09:01:01.376Z",
        "ward": "General Ward",
        "feedback": "no",
        "id": "2406-11015",
        "name": "Elias",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0765720954",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Communication was very Good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Good communication "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Excellent service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Delicious food "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "",
        "surname": "Rangwashi",
        "dateCaptured": "2024-07-31T10:00:10.477Z",
        "ward": "Surgical B",
        "comment": "No",
        "feedback": "yes",
        "id": "2431-12undefined5",
        "name": "Mabaeng",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0625728827",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 78.3,
        "emailAddress": "ndlovutsholofelo28@gmail.com",
        "surname": "Ndlovu",
        "dateCaptured": "2024-06-18T07:29:53.927Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2418-09295",
        "name": "Tsholofelo",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0672095326",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "To fastest up the porter service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "The service was perfect "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Doctor was the best he kept his promises "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "According to me cleaning service beef to improve "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Food was amazing "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 73.9,
        "emailAddress": "mahlangutebogo867@gmail.com",
        "surname": "Mahlangu ",
        "dateCaptured": "2024-09-12T07:32:58.190Z",
        "ward": "General Ward",
        "comment": "Cleaning needs to be improved and some of the nurses they offer good service but others seems as if they need more customer service ",
        "feedback": "yes",
        "id": "2412-09325",
        "name": "Lettie",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0795310124",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The guy at reception/enquiries was very rude. I had to wait for him to finish on his phone and only then he attended to me."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 74.8,
        "emailAddress": "fortupam@gmail.com",
        "surname": "Baloyi",
        "dateCaptured": "2024-08-01T07:40:11.629Z",
        "ward": "Paeds",
        "comment": "Training on customer service for reception staff.",
        "feedback": "no",
        "id": "2401-09403",
        "name": "Fortunate",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0716461489",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 85.2,
        "emailAddress": "",
        "surname": "Madiseng",
        "dateCaptured": "2024-06-22T12:10:21.777Z",
        "ward": "Maternity",
        "comment": "Please continue with your love care and support you are really changing lives for the better I really appreciate everything you did for me thank you very much may you all be blessed thank you.",
        "feedback": "yes",
        "id": "2422-14108",
        "name": "Gloria",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0715279710",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 80.9,
        "emailAddress": "keitumetsemafafo@gmail.com",
        "surname": "Mafafo",
        "dateCaptured": "2024-06-25T09:35:18.459Z",
        "ward": "Paeds",
        "comment": "Nope",
        "feedback": "yes",
        "id": "2425-11351",
        "name": "Keitumetse",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0774271584",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 78.3,
        "emailAddress": "",
        "surname": "Sithole ",
        "dateCaptured": "2024-09-13T09:27:39.170Z",
        "ward": "General Ward",
        "feedback": "no",
        "id": "2413-11275",
        "name": "Zinhle precious ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0828369176",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 90.4,
        "emailAddress": "imakhudu95@gmail.com",
        "surname": "Makhudu",
        "dateCaptured": "2024-09-07T12:52:20.093Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2407-14526",
        "name": "Itu",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0782295135",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "A special thank you to Sister Jeanette Malebye, she served us with a smile all the time. She is so passionate about serving people ⭐⭐⭐⭐⭐⭐"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 98.3,
        "emailAddress": "nathimas86@gmail.com",
        "surname": "Mampuru ",
        "dateCaptured": "2024-09-10T16:58:32.059Z",
        "ward": "Maternity",
        "feedback": "yes",
        "id": "2410-18583",
        "name": "Nkosinathi ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0737219329",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Your staff is very friendly and aproacheble "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "beupet99@gmail.com",
        "surname": "Mabunda",
        "dateCaptured": "2024-06-11T07:38:09.483Z",
        "ward": "Surgical A",
        "comment": "Keep up the good work!",
        "feedback": "yes",
        "id": "2411-09384",
        "name": "Maria ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0797454002",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good "
          }
        ],
        "overallScore": 61.7,
        "emailAddress": "jtebogo7@gmail.com",
        "surname": "Julia ",
        "dateCaptured": "2024-06-03T09:51:13.923Z",
        "ward": "General Ward",
        "comment": "No",
        "feedback": "yes",
        "id": "2403-11513",
        "name": "Tebogo",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0796972227",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 85.2,
        "emailAddress": "mosibuditseane@gmail.com",
        "surname": "Tseane ",
        "dateCaptured": "2024-09-05T14:05:34.233Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2405-16055",
        "name": "Mirriam ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0761719491",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93,
        "emailAddress": "6061 Block S Mabopane 0190",
        "surname": "Sithole",
        "dateCaptured": "2024-08-22T06:39:59.356Z",
        "ward": "Maternity",
        "feedback": "no",
        "id": "2422-08394",
        "name": "Sabina sphiwe",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0792833455",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "smangalisomthombothi2@gmail.com",
        "surname": "Mthombothi",
        "dateCaptured": "2024-06-14T09:07:18.894Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2414-11073",
        "name": "Smangaliso",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0799266577",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 80.9,
        "emailAddress": "balesengmaswabela@gmail.com",
        "surname": "Maswabela",
        "dateCaptured": "2024-07-08T13:30:36.559Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2408-15309",
        "name": "Baleseng",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0660131574",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "willowseptember5@gmail.com",
        "surname": "Sithole",
        "dateCaptured": "2024-08-21T07:13:13.344Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2421-09132",
        "name": "Lesego",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0787378933",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Excellent"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Very good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Very good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Very good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Excellent "
          }
        ],
        "overallScore": 100,
        "emailAddress": "",
        "surname": "Modungwa",
        "dateCaptured": "2024-06-27T11:21:15.603Z",
        "ward": "Surgical B",
        "comment": "Everything was good ",
        "feedback": "yes",
        "id": "2427-13211",
        "name": "Mapene",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0735568853",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "",
        "surname": "Makhubela",
        "dateCaptured": "2024-09-10T08:47:04.286Z",
        "ward": "Paeds",
        "comment": "Improve on the menu for infants",
        "feedback": "no",
        "id": "2410-10474",
        "name": "Thelma",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0685411586",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The service was good."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "We are satisfied with the nurses."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "The Doctor did a great job."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 87.8,
        "emailAddress": "boipelo.babes@gmail.com",
        "surname": "Motsumi",
        "dateCaptured": "2024-08-01T12:23:34.062Z",
        "ward": "Paeds",
        "comment": "I was satisfied with the services, continue giving good service.",
        "feedback": "yes",
        "id": "2401-14236",
        "name": "Boipelo",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0731153278",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 79.1,
        "emailAddress": "palesambemi@gmail.com",
        "surname": "Mbemi",
        "dateCaptured": "2024-09-03T13:39:59.563Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2403-15392",
        "name": "Palesa",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0797146751",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good"
          }
        ],
        "overallScore": 97.4,
        "emailAddress": "busisiwebusi@gmail.com",
        "surname": "Nkosi",
        "dateCaptured": "2024-06-12T08:35:32.094Z",
        "ward": "Paeds",
        "comment": "No everything is perfect 💯",
        "feedback": "no",
        "id": "2412-10355",
        "name": "Busisiwe",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0766111617",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 76.5,
        "emailAddress": "",
        "surname": "Maimela",
        "dateCaptured": "2024-06-21T13:14:09.804Z",
        "ward": "Surgical A",
        "feedback": "yes",
        "id": "2421-15141",
        "name": "Nancy",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0679860685",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good "
          }
        ],
        "overallScore": 90.4,
        "emailAddress": "mikemakhus2@gmail.com",
        "surname": "Makhubela ",
        "dateCaptured": "2024-07-08T09:41:27.208Z",
        "ward": "General Ward",
        "comment": "No",
        "feedback": "yes",
        "id": "2408-11415",
        "name": "Michael ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0795768008",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 91.3,
        "emailAddress": "bsngobo@gmail.com",
        "surname": "Ngobo",
        "dateCaptured": "2024-09-23T12:32:40.896Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2423-14326",
        "name": "Busisiwe",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0725038014",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 87,
        "emailAddress": "Rose.Masopye@gauteng.gov.za",
        "surname": "Masopye ",
        "dateCaptured": "2024-09-06T07:57:15.640Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2406-09576",
        "name": "Raisebe Rose ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0670021993",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 89.6,
        "emailAddress": "petrusmafemani@gmail.com",
        "surname": "Hlungwane ",
        "dateCaptured": "2024-09-11T08:12:29.518Z",
        "ward": "Surgical B",
        "comment": "No ",
        "feedback": "yes",
        "id": "2411-10121",
        "name": "Mafemani Petrus",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0735311067",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "They performing Batho Pele principles in their Organization\n\n\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "When I Ring the Bell, there where there before I can penic "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Explained everything I needed to know. And make me understand "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 80.9,
        "emailAddress": "mokgadirb@gmail.com",
        "surname": "Matsena",
        "dateCaptured": "2024-07-10T09:27:18.195Z",
        "ward": "Maternity",
        "feedback": "no",
        "id": "2410-11272",
        "name": "Mokgadi",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0602601516",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "Seemaomolemo3@gmail.com",
        "surname": "Seema ",
        "dateCaptured": "2024-09-13T11:07:27.277Z",
        "ward": "Surgical B",
        "comment": "No",
        "feedback": "yes",
        "id": "2413-13075",
        "name": "Omolemo ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0607654063",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 89.6,
        "emailAddress": "yespersey@gmail.com",
        "surname": "Mashale",
        "dateCaptured": "2024-09-03T09:22:51.877Z",
        "ward": "NICU",
        "feedback": "yes",
        "id": "2403-11226",
        "name": "Perseverence",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0728015401",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Keep up the good services and excellent work"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Happy with service rendered with smile and courtsey"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Excellent service "
          }
        ],
        "overallScore": 100,
        "emailAddress": "cynthiamukondeleli@gmail.com",
        "surname": "Ngobeni",
        "dateCaptured": "2024-06-18T06:39:56.962Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2418-08391",
        "name": "Mukondeleli cynthia",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0693609618",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 89.6,
        "emailAddress": "",
        "surname": "Maheso su",
        "dateCaptured": "2024-09-17T10:23:17.365Z",
        "ward": "General Ward",
        "comment": "Kitchen  improvement ",
        "feedback": "yes",
        "id": "2417-12234",
        "name": "Dorothy ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0661396510",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 86.1,
        "emailAddress": "seshokatj@gmail.com",
        "surname": "Seshoka",
        "dateCaptured": "2024-07-02T09:31:49.858Z",
        "ward": "General Ward",
        "comment": "Keep it up",
        "feedback": "no",
        "id": "2402-11311",
        "name": "Thabang",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0818865823",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 98.3,
        "emailAddress": "Depuo.Modau@gauteng.gov.za",
        "surname": "Modau",
        "dateCaptured": "2024-09-18T12:15:00.837Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2418-14158",
        "name": "Depuo",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0832063961",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Nurse rakgadi is the best "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "He was not clear "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 92.2,
        "emailAddress": "hlengiwekhumalo149@gmail.com",
        "surname": "Sangweni",
        "dateCaptured": "2024-08-26T10:39:29.689Z",
        "ward": "Surgical B",
        "feedback": "no",
        "id": "2426-12398",
        "name": "Gugu",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0824288782",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Extremely good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "They were explaining everything "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "All the time "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "May the good Lord bless them "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Extremely good taste "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "na",
        "surname": "Maseko",
        "dateCaptured": "2024-06-26T12:16:42.546Z",
        "ward": "General Ward",
        "comment": "Create therapeutic environments for patients ",
        "feedback": "yes",
        "id": "2426-14165",
        "name": "Mmatlala",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0767748174",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "lebogangmashiloane554@gmail.com",
        "surname": "Mashiloane",
        "dateCaptured": "2024-09-10T09:53:59.194Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2410-11534",
        "name": "Lebogang ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0738101426",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good "
          }
        ],
        "overallScore": 91.3,
        "emailAddress": "constancekekana36@gmail.com",
        "surname": "Msimango",
        "dateCaptured": "2024-07-31T10:54:18.443Z",
        "ward": "Surgical B",
        "comment": "No",
        "feedback": "yes",
        "id": "2431-12541",
        "name": "Constance",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0794040371",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Thank you very much for your hospitality\nY'all take very care of your patients \nGod bless you "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Thank you again for your care"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "The doctors really know their job thank you"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Thank you to all the cleaners "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "May god bless you \nThank you so much"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "I would highly recommended it\nOne of the best hospital "
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "treciamatsie123@gmail.com",
        "surname": "Matsie",
        "dateCaptured": "2024-06-30T08:13:36.955Z",
        "ward": "General Ward",
        "comment": "Their service is good ",
        "feedback": "yes",
        "id": "2430-10133",
        "name": "Selemagae",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0760764523",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "They are very efficient and notify mom and dad of all I was worried about."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "They explained all medication and doses and reasons behind the administration."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "You have amazing peads doctors, the one attending to my needs is friendly, informative and calmed my nerves"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Non comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "It's typical hospital food LMAO 🤣"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Typical kids ward"
          }
        ],
        "overallScore": 96.5,
        "emailAddress": "kgomotso2008@hotmail.com",
        "surname": "Makondo",
        "dateCaptured": "2024-08-13T16:12:49.123Z",
        "ward": "Paeds",
        "comment": "None ",
        "feedback": "yes",
        "id": "2413-18121",
        "name": "Sandra",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0796587879",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "soulsphiwe7@gmail.com",
        "surname": "Mthombeni",
        "dateCaptured": "2024-08-07T06:22:22.082Z",
        "ward": "Surgical A",
        "feedback": "no",
        "id": "2407-08223",
        "name": "Sphiee",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0714129622",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "mnicky0@gmail.com",
        "surname": "Mondlane",
        "dateCaptured": "2024-08-13T12:08:06.449Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2413-14082",
        "name": "Nonkululeko Nicole",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0798162515",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "",
        "surname": "Chauke",
        "dateCaptured": "2024-09-13T09:31:41.868Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2413-11314",
        "name": "Elizabeth ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0721084136",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Staff was Caring I didn't feel like an outcast , they staff\n were very  welcoming. Keep up the good work that ur doing  and also do to others   thanks  from ls rakgoale\n\n\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "lorrainerakgoale ag mail.com",
        "surname": "Rakgoale",
        "dateCaptured": "2024-08-22T12:23:00.293Z",
        "ward": "Maternity",
        "comment": "Nothing thanks",
        "feedback": "yes",
        "id": "2422-14236",
        "name": "Lorraine",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0790849413",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 86.1,
        "emailAddress": "suzanmpitjeng@gmail.com",
        "surname": "Pitjeng",
        "dateCaptured": "2024-06-25T07:49:06.853Z",
        "ward": "Paeds",
        "comment": "No",
        "feedback": "yes",
        "id": "2425-09499",
        "name": "Mmathapelo",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0799514594",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I was happy\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Ventilation "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "",
        "surname": "Modiba",
        "dateCaptured": "2024-08-24T13:02:57.880Z",
        "ward": "Surgical A",
        "feedback": "yes",
        "id": "2424-15025",
        "name": "Peter",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0637547708",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "No comment "
          }
        ],
        "overallScore": 74.8,
        "emailAddress": "katemathonsi@yahoo.com",
        "surname": "Khoza",
        "dateCaptured": "2024-07-18T07:31:36.220Z",
        "ward": "Paeds",
        "comment": "No comment ",
        "feedback": "no",
        "id": "2418-09314",
        "name": "Nokubonga kate",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0733197",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Marvelous "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "None"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Extremely good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Well served "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Very good "
          }
        ],
        "overallScore": 94.8,
        "emailAddress": "",
        "surname": "Masemola",
        "dateCaptured": "2024-06-26T13:03:32.128Z",
        "ward": "General Ward",
        "comment": "Extra good may the good Lord bless ",
        "feedback": "yes",
        "id": "2426-15031",
        "name": "Shimane",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0725854680",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "motswapulengdinyana@.com",
        "surname": "Motswapuleng",
        "dateCaptured": "2024-09-03T13:33:44.252Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2403-15335",
        "name": "Pauline",
        "reference": "Search engine (e.g., Google or Bing)"
      },
      {
        "cellphonenumber": "0766009269",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Satisfying "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "I'm happy about the service"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Satisfied "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "mgwenyapatricia300.@gmai.com",
        "surname": "Mgwenya",
        "dateCaptured": "2024-09-11T13:06:26.218Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2411-15067",
        "name": "Patricia",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0786019786",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Yes"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "In details he did explain"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Very neat"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Enjoyed every meal."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "I do recommend my colleagues."
          }
        ],
        "overallScore": 100,
        "emailAddress": "koketso.maake@gauteng.gov.za",
        "surname": "Maake",
        "dateCaptured": "2024-09-05T10:58:28.794Z",
        "ward": "Surgical B",
        "comment": "No service is satisfying",
        "feedback": "no",
        "id": "2405-12581",
        "name": "Dorothy",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0767310163",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "noganewabobedi2@gmail.com",
        "surname": "Makua",
        "dateCaptured": "2024-06-10T13:12:18.600Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2410-15126",
        "name": "Huffi",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0796052604",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 67.8,
        "emailAddress": "yandamazibuko@icloud.com",
        "surname": "Mazibuko ",
        "dateCaptured": "2024-07-28T15:15:52.121Z",
        "ward": "General Ward",
        "id": "2428-17155",
        "name": "SINENHLANHLA ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0815926676",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The staff was very professional, helpful and nice"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 95.7,
        "emailAddress": "atngobene@gmail.com",
        "surname": "Ngobene",
        "dateCaptured": "2024-06-14T09:33:14.450Z",
        "ward": "Paeds",
        "comment": "",
        "feedback": "no",
        "id": "2414-11336",
        "name": "Andelsia tshepo",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0733426248",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "",
        "surname": "Mkhonza",
        "dateCaptured": "2024-09-11T07:41:54.663Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2411-09414",
        "name": "Aubrey",
        "reference": "Facebook"
      },
      {
        "cellphonenumber": "0720598428",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "N/A"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "N/A"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "N/A"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "\nThey must in prove clean the toilet pls"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "In the morning they must service porridge "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Very good "
          }
        ],
        "overallScore": 88.7,
        "emailAddress": "ngoebanemaria@gmail.com",
        "surname": "Ngoebane",
        "dateCaptured": "2024-09-02T10:28:11.114Z",
        "ward": "Paeds",
        "comment": "N/A",
        "feedback": "no",
        "id": "2402-12285",
        "name": "Maria",
        "reference": "Facebook"
      },
      {
        "cellphonenumber": "0711869264",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Avarage"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Excellent \n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Fair "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Fair"
          }
        ],
        "overallScore": 59.1,
        "emailAddress": "mamaitu0507.gmail.com",
        "surname": "Napo",
        "dateCaptured": "2024-08-28T11:23:28.934Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2428-13233",
        "name": "Itumeleng",
        "reference": "Facebook"
      },
      {
        "cellphonenumber": "0678989766",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Happy with the services "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Happy with the Doctors service . excellent 👌"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "The. Mother's toilet needs a little more improvement "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "The food are fair."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Overall I am happy with the services,/accomodations "
          }
        ],
        "overallScore": 89.6,
        "emailAddress": "kgomotsotsoku@gmail.com",
        "surname": "Tsoku",
        "dateCaptured": "2024-09-12T10:15:59.854Z",
        "ward": "Paeds",
        "comment": "To improve kitchen staff services, attitude.",
        "feedback": "no",
        "id": "2412-12153",
        "name": "kgomotso",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0818747759",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "lenahletsoalo@yahoo.com",
        "surname": "Masina",
        "dateCaptured": "2024-09-08T09:30:57.912Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2408-11305",
        "name": "Lena",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0682813698",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 66.1,
        "emailAddress": "maphokutlwano@gmail.com",
        "surname": "Maphumulo ",
        "dateCaptured": "2024-08-30T08:04:34.588Z",
        "ward": "General Ward",
        "feedback": "no",
        "id": "2430-10046",
        "name": "Kutlwano ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0722782487",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "No comment "
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "abiottabane@gmail..com",
        "surname": "Tabane",
        "dateCaptured": "2024-08-09T08:01:55.533Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2409-10019",
        "name": "Abiot",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0824162441",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 80.9,
        "emailAddress": "thabangconny@gmail.com",
        "surname": "Masala",
        "dateCaptured": "2024-09-20T17:13:51.107Z",
        "ward": "NICU",
        "comment": "It would be good if the nurses could stop wearing perfume, because some people have allergies.\nNot long ago I was affected by the perfume worn by the nurse at NICU.\nShe put on too much perfume.\nCan the nurses not put on too much perfume or don't put any at all.\n",
        "feedback": "yes",
        "id": "2420-19139",
        "name": "Thabang",
        "reference": "Facebook"
      },
      {
        "cellphonenumber": "0714755115",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Poor service from sister reginah mamaleka \n, Shouting at my child and myself. Falsely accusing me of removing the drip when I did not, not bothered to apologize after explaining that the drip was removed by another nurse. Confronted her of the tone she used and had no remorse. Very bad attitude.0\n\n\n\n\n\n\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Nup"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Sheets not changed since Sunday on arrival "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 50.4,
        "emailAddress": "lekalakalahunadi87@gmail.com",
        "surname": "Lekalakala",
        "dateCaptured": "2024-08-21T10:37:47.462Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2421-12378",
        "name": "Hunadi",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0721249965",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "No comments "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "No comments "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "No comments "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "No comments "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "No comments "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "No comnments "
          }
        ],
        "overallScore": 72.2,
        "emailAddress": "",
        "surname": "Nkwani",
        "dateCaptured": "2024-06-24T12:11:16.889Z",
        "ward": "General Ward",
        "comment": "No",
        "feedback": "yes",
        "id": "2424-14118",
        "name": "Rosina",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0655617195",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Some of the nurses were fine and others were not.\nI'm extremely happy by the treatment we got from Sr Mabena and Sr Kedibone,I highly recommended their service"
          }
        ],
        "overallScore": 64.3,
        "emailAddress": "sibongilelucia06@gmail.com",
        "surname": "Dlamini",
        "dateCaptured": "2024-06-25T07:39:27.919Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2425-09394",
        "name": "Sibongile",
        "reference": "Search engine (e.g., Google or Bing)"
      },
      {
        "cellphonenumber": "0835694683",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Friendly reception, good service, everything was handled carefully with smile"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good and quality treatment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Smart  and excellent  care"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Very neat and satisfying environment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Very good "
          }
        ],
        "overallScore": 100,
        "emailAddress": "salomempya082@gmail",
        "surname": "Mpya",
        "dateCaptured": "2024-08-23T10:49:28.180Z",
        "ward": "Surgical B",
        "comment": "Keep up the good service, Now I know my condition and how to take care of my self",
        "feedback": "yes",
        "id": "2423-12494",
        "name": "Salome",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0765652607",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Regina,fina and Tebogo made our stay comfortable. They explained everything to us clearly, were friendly. They are what the hospital needs"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "consolationgabara@gmail.com",
        "surname": "Mulamavhu gabara",
        "dateCaptured": "2024-06-01T14:22:36.413Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2401-16224",
        "name": "Consolation",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0699279304",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I feel like if someone comes with an emergency they should be treated right away not be kept waiting hence we avoid going to public hospitals"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 77.4,
        "emailAddress": "1287caroline@gmail.com",
        "surname": "Mondlana",
        "dateCaptured": "2024-08-21T06:29:51.854Z",
        "ward": "Paeds",
        "comment": "Nothing besides the point I made before",
        "feedback": "no",
        "id": "2421-08294",
        "name": "Shiela",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0781675310",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The nurses were very helpful and welcoming "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Yes everything was explained "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "She didn't explain "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "The food was presentable with 3 star hotels\n\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Yes I would recommend the hospital for friends and family "
          }
        ],
        "overallScore": 84.3,
        "emailAddress": "mmapula.maluluke@yahoo.com",
        "surname": "Maluleke",
        "dateCaptured": "2024-08-29T13:21:59.451Z",
        "ward": "Paeds",
        "comment": "No comment for now",
        "feedback": "yes",
        "id": "2429-15214",
        "name": "Oniccah",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0798203673",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "None"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "None"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Na"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "None"
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "",
        "surname": "Sibanda",
        "dateCaptured": "2024-09-13T09:55:34.774Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2413-11551",
        "name": "Olivia ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0724540996",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "1.1. How would you rate the friendliness and courtesy of the reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "radio",
                "score": "5",
                "number": ""
              }
            ],
            "section": "2. How long did the staff take before they attended to you?",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "3.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "3.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "3. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "4.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "4. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the cleanliness of the ER unit and bathroom facilities"
              }
            ],
            "section": "5. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. How would you rate your experience at our ER Unit"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.2. How would you rate the duration of the consultation process our ER Unit"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.3. How likely are you to recommend our hospital to your family and friends"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.4. Have you visited Botshilu Private Hospital before"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.5. Did any of our staff members go out of their way to make you feel special during your visit at our ER Unit"
              },
              {
                "answer": "text",
                "score": "Dr",
                "number": "6.6. Name of the staff member"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 78.8,
        "emailAddress": "eddie.ramothwala@phelangbonolo.co.za",
        "surname": "Ramothwala",
        "dateCaptured": "2024-05-28T12:16:58.322Z",
        "ward": "Emergency Unit (ER)",
        "feedback": "no",
        "id": "2428-14166",
        "name": "Eddie",
        "reference": "Facebook"
      },
      {
        "cellphonenumber": "0648924892",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Service was excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "",
        "surname": "Nobela",
        "dateCaptured": "2024-07-02T08:02:47.371Z",
        "ward": "General Ward",
        "comment": "Not yet now ",
        "feedback": "yes",
        "id": "2402-10029",
        "name": "Edwin",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0715815858",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I appreciate effort from friendly staff "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Much appreciated "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Impressive "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "The hospital is officially my home, whenever I need support "
          }
        ],
        "overallScore": 98.3,
        "emailAddress": "pptshiamoo@gmail.com",
        "surname": "Mathebula",
        "dateCaptured": "2024-06-10T14:33:28.216Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2410-16337",
        "name": "Solly",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0794289919",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Thank you, nurses and doctor's for your care and dedication."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "I'm greatful for ur passion for healthcare "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "I appreciate your efforts and excellent care Dr Morulane and his caring team"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Ur kindness has made a difference "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Thank u for everything u doing for your patients ❤️🙏"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Thank you all for being on the front line care.thank you for your kindness and Alway putting your patients first "
          }
        ],
        "overallScore": 100,
        "emailAddress": "nolomalema@gmail.com",
        "surname": "Malema",
        "dateCaptured": "2024-09-10T08:31:26.423Z",
        "ward": "Paeds",
        "comment": "Keep up the good work,I'm so proud of you all.😍🥰😘💗👌",
        "feedback": "yes",
        "id": "2410-10311",
        "name": "Letlhogonolo audrey",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0837126078",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 97.4,
        "emailAddress": "mashishilucia@gmail.com",
        "surname": "Mashishi",
        "dateCaptured": "2024-09-04T09:27:32.970Z",
        "ward": "Surgical B",
        "feedback": "no",
        "id": "2404-11277",
        "name": "Lucia",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0606926396",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "salomeroro@icloud.com",
        "surname": "Pole",
        "dateCaptured": "2024-06-25T14:53:08.151Z",
        "ward": "Maternity",
        "comment": "No",
        "feedback": "yes",
        "id": "2425-16538",
        "name": "Rorisang",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0782273621",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "I had to request for the cleaning of the kitchen even taking out of trash from day shift, the lady have an attitude very rude does not even great."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Food arrives very cold, we were sometimes given food of their choice when we ask I was told they decided for mecthe menu."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "The ward did not even have blanket for kids"
          }
        ],
        "overallScore": 75.7,
        "emailAddress": "",
        "surname": "Malingane",
        "dateCaptured": "2024-07-16T09:53:40.830Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2416-11531",
        "name": "Joyce",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0606362511",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "All the nurses were friendly"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "",
        "surname": "Malatsi",
        "dateCaptured": "2024-06-22T10:55:50.866Z",
        "ward": "Maternity",
        "feedback": "yes",
        "id": "2422-12555",
        "name": "Shadi",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0767909016",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Check the condition or design of the shower head. Not user friendly\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "20% juice taste horrible\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 90.4,
        "emailAddress": "promisemashimbi@yahoo.com",
        "surname": "Mashimbi",
        "dateCaptured": "2024-06-07T13:58:21.062Z",
        "ward": "Maternity",
        "feedback": "no",
        "id": "2407-15585",
        "name": "Promise",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0694546356",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 84.3,
        "emailAddress": "",
        "surname": "Modise",
        "dateCaptured": "2024-08-07T12:42:37.128Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2407-14422",
        "name": "Refilwe",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0724993324",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Ye but I'm "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "I'm satisfied with she was very good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Yes"
          }
        ],
        "overallScore": 87.8,
        "emailAddress": "mkgoele@justice.gov.za MI",
        "surname": "Kgoele",
        "dateCaptured": "2024-08-09T10:49:14.786Z",
        "ward": "Paeds",
        "comment": "No",
        "feedback": "yes",
        "id": "2409-12496",
        "name": "Margaret ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0767920725",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 90.4,
        "emailAddress": "juliah.maswanganye@gmail.com",
        "surname": "Baloyi",
        "dateCaptured": "2024-08-29T13:31:34.993Z",
        "ward": "Paeds",
        "comment": "I am not happy with the way you moved my son from one ward to another because he ended up catching an infection that he didn't come with. I am very angry, it was like I am not paying as he was moved to up to 5 rooms between two wards; the paeds and surgical B ward. Please for your patients' sake stop moving them around wards",
        "feedback": "yes",
        "id": "2429-15312",
        "name": "Juliah",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0722518436",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Good service"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Excellent"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Clean"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Delicious"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good"
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "p.madute@gmail.com",
        "surname": "Madute",
        "dateCaptured": "2024-07-19T12:40:18.248Z",
        "ward": "Surgical B",
        "comment": "Service was excellent",
        "feedback": "yes",
        "id": "2419-14408",
        "name": "Patrick ",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0603304201",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Well received "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "On my last day,I'm dissatisfied about the treatment.two drips tried to be inserted but failed. Sister in charge left me with a swollen and painful hand. I never received the days medication because of the failed drips."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "bm.monza@gmail.com",
        "surname": "Motlhatlhedi",
        "dateCaptured": "2024-07-08T16:27:41.485Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2408-18273",
        "name": "Boitumelo ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0818932142",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Keep up the good work "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "All staff where friendly and helpful ..molebogeng"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Keep up the good work "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Keep up the good work "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Keep up the good service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "ruthd.mokgoshi@gmail.com",
        "surname": "Mokgoshi",
        "dateCaptured": "2024-09-11T07:55:11.665Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2411-09559",
        "name": "Ruth",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0788509320",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Excellent service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "None"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Money "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Cleanings to be improved "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Cooking to be improved "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "None "
          }
        ],
        "overallScore": 86.1,
        "emailAddress": "joycedtinti@gmail.com",
        "surname": "Ditinti",
        "dateCaptured": "2024-06-26T13:24:14.355Z",
        "ward": "General Ward",
        "comment": "None ",
        "feedback": "yes",
        "id": "2426-15243",
        "name": "Joyce",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0760703024",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Extremely good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "May the good Lord bless you "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Very good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Very good "
          }
        ],
        "overallScore": 94.8,
        "emailAddress": "na",
        "surname": "Skosau",
        "dateCaptured": "2024-06-26T09:04:34.346Z",
        "ward": "General Ward",
        "comment": "Keep it up the good services ",
        "feedback": "yes",
        "id": "2426-11047",
        "name": "Esther ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0675744946",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 97.4,
        "emailAddress": "mampalerato43@gmail.com",
        "surname": "Mampa",
        "dateCaptured": "2024-07-30T06:08:22.829Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2430-08081",
        "name": "Lerato",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0824316663",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Very good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Very good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good"
          }
        ],
        "overallScore": 98.3,
        "emailAddress": "",
        "surname": "Mokhonoana",
        "dateCaptured": "2024-09-03T11:24:39.274Z",
        "ward": "Surgical B",
        "feedback": "no",
        "id": "2403-13243",
        "name": "Mamahlola",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0799950204",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "priscillamosima58@gmail.com",
        "surname": "Mathatho",
        "dateCaptured": "2024-09-08T08:10:03.411Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2408-10107",
        "name": "Priscilla mosima",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0636986943",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Yes , the Dr. Has explain the condition of the the baby."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "The food are well served."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "The hospital will be recommended to friends and family\n"
          }
        ],
        "overallScore": 88.7,
        "emailAddress": "entlesylvia@gmail.com",
        "surname": "Kutu",
        "dateCaptured": "2024-08-21T10:27:45.196Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2421-12271",
        "name": "Sylvia",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0636207277",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 92.2,
        "emailAddress": "ntombidube51@gmail.com",
        "surname": "Dube",
        "dateCaptured": "2024-09-12T11:48:27.498Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2412-13489",
        "name": "Ntombifuthi",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0722189506",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "radio",
                "score": "5",
                "number": ""
              }
            ],
            "section": "2. How long did the staff take before they attended to you?",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "3.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "3. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "4.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "4. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the cleanliness of the ER unit and bathroom facilities"
              }
            ],
            "section": "5. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. How would you rate your experience at our ER Unit"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.2. How would you rate the duration of the consultation process our ER Unit"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.3. How likely are you to recommend our hospital to your family and friends"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.4. Have you visited Botshilu Private Hospital before"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.5. Did any of our staff members go out of their way to make you feel special during your visit at our ER Unit"
              },
              {
                "answer": "text",
                "score": "Er stuff that was on duty",
                "number": "6.6. Name of the staff member"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "All of them "
          }
        ],
        "overallScore": 95.3,
        "emailAddress": "",
        "surname": "Nkomo",
        "dateCaptured": "2024-09-20T07:18:46.035Z",
        "ward": "Emergency Unit (ER)",
        "feedback": "yes",
        "id": "2420-09184",
        "name": "Gcinaphi",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0664497400",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 87.8,
        "emailAddress": "valleriemasina4@gmail.com",
        "surname": "Khoza",
        "dateCaptured": "2024-05-31T13:02:25.675Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2431-15028",
        "name": "Vallerie",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0681196903",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 79.1,
        "emailAddress": "kelebogilelizah22@gmail.com",
        "surname": "Mamabolo",
        "dateCaptured": "2024-09-05T06:50:11.990Z",
        "ward": "NICU",
        "feedback": "no",
        "id": "2405-08505",
        "name": "Kelebogile",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0766248421",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "The food we really good for the kids to but also difficult for them to chow "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 89.6,
        "emailAddress": "karabomokgoko96@gmail.com.za",
        "surname": "Mokgok",
        "dateCaptured": "2024-09-07T12:17:47.431Z",
        "ward": "Paeds",
        "comment": "No",
        "feedback": "yes",
        "id": "2407-14175",
        "name": "Karabo",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "071",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "They explain all things how they help  people  "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "No comment was good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "The room is clean "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "No comment is good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "No comment "
          }
        ],
        "overallScore": 80,
        "emailAddress": "",
        "surname": "Mputla",
        "dateCaptured": "2024-06-12T10:33:04.415Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2412-12337",
        "name": "Tebogo martha",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0607654063",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 94.8,
        "emailAddress": "yespersey@gmail.com",
        "surname": "Mashale",
        "dateCaptured": "2024-08-30T13:07:50.134Z",
        "ward": "Maternity",
        "comment": "Thank you.\n\nKea leboga.\n\nNo livhua.",
        "feedback": "yes",
        "id": "2430-15074",
        "name": "Perseverence",
        "reference": "Search engine (e.g., Google or Bing)"
      },
      {
        "cellphonenumber": "0744850573",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Staff was friendly and helpful"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "No comments"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Highly recommend "
          }
        ],
        "overallScore": 100,
        "emailAddress": "Thibediselepe@gmail.com",
        "surname": "Selepe",
        "dateCaptured": "2024-07-19T09:34:53.920Z",
        "ward": "Surgical B",
        "comment": "No",
        "feedback": "yes",
        "id": "2419-11348",
        "name": "Raymond Thibedi",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0796447135",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I was satisfy with the admission and the rules "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "They did explain everything about the medication and the effects "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Yes she did explain everything and I understood everything "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "They were friendly and kind"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "I was not satisfy with meatballs"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "It's the best hospital and the service is good "
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "thulisilemoabi422@gmail.com",
        "surname": "Moabi",
        "dateCaptured": "2024-05-28T13:06:27.373Z",
        "ward": "Paeds",
        "comment": "No",
        "feedback": "yes",
        "id": "2428-15063",
        "name": "Tolly",
        "reference": "Search engine (e.g., Google or Bing)"
      },
      {
        "cellphonenumber": "0769527011",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The staff was excellent from cleaning to the nursing staff "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Everything were good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Everything were good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Everything were good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Everything were good "
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "1904 block H soshanguve",
        "surname": "Hlophe",
        "dateCaptured": "2024-05-31T08:14:57.386Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2431-10146",
        "name": "Lucky",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0722931731",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 91.3,
        "emailAddress": "makhubelenooita@gmail.com",
        "surname": "Makhubele",
        "dateCaptured": "2024-06-10T10:56:23.926Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2410-12563",
        "name": "Nooita",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0768618275",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Method used at reception,I suggest you give clients numerical numbers to control the queue "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Impressive "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Clean"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Done"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Excellent "
          }
        ],
        "overallScore": 72.2,
        "emailAddress": "tshetlhum@gmail.com",
        "surname": "Masimeni",
        "dateCaptured": "2024-06-19T14:17:20.648Z",
        "ward": "Surgical B",
        "comment": "Improve on reception movement for clients ",
        "feedback": "yes",
        "id": "2419-16174",
        "name": "Tshehlo",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0608241843",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Let's improve the wifi connection "
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "sheerlordg@gmail.com",
        "surname": "Gaba",
        "dateCaptured": "2024-07-08T12:50:40.903Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2408-14508",
        "name": "Sheerlord",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0792637272",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "",
        "surname": "Mbembe",
        "dateCaptured": "2024-06-20T14:01:54.938Z",
        "ward": "Surgical A",
        "comment": "Nurses were very good especially nurse Rammala",
        "feedback": "yes",
        "id": "2420-16019",
        "name": "Washington ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0798772835",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Fair"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 83.5,
        "emailAddress": "mabopane",
        "surname": "Testing",
        "dateCaptured": "2024-06-03T05:20:24.145Z",
        "ward": "General Ward",
        "comment": "Reduce noise",
        "feedback": "yes",
        "id": "2403-07209",
        "name": "Test",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0727945371",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Am so happy with the nursing staff they are so friendly "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "They didn't explain "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "I love the doctor so much  she explained everything to me with good understanding "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "The food for kids IA not good they serve dry food for kids and taste and quality of meals is not Good at all am not happy  because am paying  for the food at the end the baby  don't eat the food  am ever disappointed "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "It fine"
          }
        ],
        "overallScore": 68.7,
        "emailAddress": "Gontse.mosia95@gmail.com",
        "surname": "Mosia",
        "dateCaptured": "2024-09-18T12:11:54.689Z",
        "ward": "Paeds",
        "comment": "There must change the food  that give to kids  it not fine  ",
        "feedback": "yes",
        "id": "2418-14117",
        "name": "Gontse",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0684638100",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 98.3,
        "emailAddress": "matjiesetshabe@gmail.com",
        "surname": "Matjie",
        "dateCaptured": "2024-07-26T08:45:58.410Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2426-10455",
        "name": "Setshabe",
        "reference": "Facebook"
      },
      {
        "cellphonenumber": "0731915786",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "moipone702@gmail.com",
        "surname": "Mbiza",
        "dateCaptured": "2024-09-02T11:02:16.055Z",
        "ward": "NICU",
        "feedback": "yes",
        "id": "2402-13027",
        "name": "Moipone",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0828437168",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The staff is so welcoming and friendly "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "They nurses made sure that I understand each and everything "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "The doctors were really attending to each and everything. They made sure everything is okay"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "They made sure we have the hand soap,the paper towel and the place is clean all the time"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "The service at this hospital is good and the staff too is welcoming"
          }
        ],
        "overallScore": 97.4,
        "emailAddress": "",
        "surname": "Mabone",
        "dateCaptured": "2024-08-06T08:54:43.634Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2406-10542",
        "name": "Cathrine",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0824162441",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "\nThere's no much I can say, but my baby is catered for. He is fed on time everyday "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 81.7,
        "emailAddress": "thabangconny@gmail.com",
        "surname": "Masala",
        "dateCaptured": "2024-09-14T12:02:06.599Z",
        "ward": "NICU",
        "comment": "I would suggest that nurses work the same. Give the doctors report if there's one,and not to wait for the patient or the parent to the patient to ask questions",
        "feedback": "yes",
        "id": "2414-14021",
        "name": "Thabang",
        "reference": "Facebook"
      },
      {
        "cellphonenumber": "0767032715",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 95.7,
        "emailAddress": "0769901383.mr@gmail.com",
        "surname": "Ramosunya",
        "dateCaptured": "2024-08-28T09:11:13.009Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2428-11117",
        "name": "Masenki",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0798127923",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Need some more improvement "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "I'm satisfied "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Need some more improvement "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "I m not impressed "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "I m not impressed "
          }
        ],
        "overallScore": 70.4,
        "emailAddress": "nhlamulogift.mashaba@gmail.com",
        "surname": "Mashaba",
        "dateCaptured": "2024-08-28T08:44:39.338Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2428-10449",
        "name": "Hlamulo",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0630582764",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 97.4,
        "emailAddress": "cneo972@gmail.com",
        "surname": "Masango",
        "dateCaptured": "2024-07-16T12:02:35.066Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2416-14029",
        "name": "Carly",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0789775809",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "louisarm45@gmail.com",
        "surname": "Mashilo",
        "dateCaptured": "2024-08-06T09:09:08.674Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2406-11096",
        "name": "Louisa Raisebe",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0603925190",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 77.4,
        "emailAddress": "nkhensanimaluleka1@gmail.cm",
        "surname": "Ngobeni",
        "dateCaptured": "2024-08-27T12:38:07.302Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2427-14386",
        "name": "Nkhensani",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0791173919",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Yes I was ,cause it was fast and quick."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Everything was on time .."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "forgivenonhlanhla14@gmail.com",
        "surname": "Khoza",
        "dateCaptured": "2024-06-06T10:54:03.377Z",
        "ward": "Maternity",
        "comment": "Everything was on point 🥳Thank you ",
        "feedback": "yes",
        "id": "2406-12543",
        "name": "Forgive",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0646534505",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "She explained it's purpose but forgot to tell me about any potential side effects ."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Not yet."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Every day. "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "The food here is AMAZING but the temperature 😬.the food is always warm and not hot."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "I'm gonna tell everyone about this hospital 🏥 "
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "MuhleArmy@gmail.com",
        "surname": "Mogashoa ",
        "dateCaptured": "2024-08-28T09:42:50.143Z",
        "ward": "General Ward",
        "comment": "This hospital is great but there are a few suggestions I would like give. \n1. Please fix food temperature (give us the food while it is still hot)\n2. Please fix the water pressure of the shower 🚿 and make the shower head a bit bigger .",
        "feedback": "yes",
        "id": "2428-11423",
        "name": "Muhle ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0724664072",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "",
        "surname": "Kekana",
        "dateCaptured": "2024-09-17T16:39:37.810Z",
        "ward": "Surgical B",
        "feedback": "no",
        "id": "2417-18394",
        "name": "Frans",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0692729867",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Best Service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Great "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "He explained every procedure in details"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Small portions"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Strongly recommend "
          }
        ],
        "overallScore": 86.1,
        "emailAddress": "sandydayspa4@gmail.com",
        "surname": "Makumbe",
        "dateCaptured": "2024-06-22T12:25:49.324Z",
        "ward": "Maternity",
        "comment": "Excellent Service from maternity staff, theatre and Doctors \n",
        "feedback": "yes",
        "id": "2422-14254",
        "name": "Musandiwa",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0726360108",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "I was helped by the lady called \" mme maleke\" that lady knows her job she knows why she came to work cause, she was always on our room making sure that my child is okay even in other rooms"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "But I felt like some Drs were rushing to just fill those claim forms, they didn't engage much with me...some came to the room for like 2 minutes then went to the nursing station to fill those claiming forms and please normalize checking those CCTV cameras "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "The bathroom was always clean and smelling nice the staff were also friendly "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "I was surprised when I was told that I need to pay extra R20 for each meal(morning ,lunch and supper)inorder for me to eat( they wanted cash they didn't want me to pay via eft) imagine we were admitted for 7 days.. imagine the money I payed ... honestly speaking I don't understand that part..."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "I don't think I'll think I'll come back again"
          }
        ],
        "overallScore": 67,
        "emailAddress": "nomsanteta@gmail.com",
        "surname": "Nteta ",
        "dateCaptured": "2024-09-23T02:10:11.012Z",
        "ward": "Paeds",
        "comment": "1 Please fix the food issue \n2 I wasn't happy with some of your staff and I won't mention names, some really need some serious training ,I mean you can't  play loud music or talking about your personal life in front of patients ..that's very unprofessional \n3 Gums,,🚩 very unprofessional \n4 Teach them how to speak \n",
        "feedback": "yes",
        "id": "2423-04105",
        "name": "Precious ",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0844779332",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "radio",
                "score": "5",
                "number": ""
              }
            ],
            "section": "2. How long did the staff take before they attended to you?",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "3. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "4.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "4. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the cleanliness of the ER unit and bathroom facilities"
              }
            ],
            "section": "5. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. How would you rate your experience at our ER Unit"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.2. How would you rate the duration of the consultation process our ER Unit"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.3. How likely are you to recommend our hospital to your family and friends"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.4. Have you visited Botshilu Private Hospital before"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.5. Did any of our staff members go out of their way to make you feel special during your visit at our ER Unit"
              },
              {
                "answer": "text",
                "score": "Cecilia",
                "number": "6.6. Name of the staff member"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Welcome with a smile and care"
          }
        ],
        "overallScore": 94.1,
        "emailAddress": "",
        "surname": "Choshi",
        "dateCaptured": "2024-09-20T07:28:46.389Z",
        "ward": "Emergency Unit (ER)",
        "feedback": "yes",
        "id": "2420-09288",
        "name": "Mologadi",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0760174611",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "na",
        "surname": "Nyembe",
        "dateCaptured": "2024-09-03T07:56:55.431Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2403-09563",
        "name": "Nompendulo",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0795874899",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "We waited at casualty for a long time"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "The nursing personal were kind and nice especially sister Khabonina Bhila,she's professional and friendly"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Perfect"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Great service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 94.8,
        "emailAddress": "shadeetshela@gmail.com",
        "surname": "Thela",
        "dateCaptured": "2024-09-02T08:08:33.708Z",
        "ward": "Surgical B",
        "comment": "No",
        "feedback": "yes",
        "id": "2402-10089",
        "name": "Shadrack",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0679090236",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Well explained\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Nonn"
          }
        ],
        "overallScore": 100,
        "emailAddress": "koki.khunana91@gmail",
        "surname": "Khunana",
        "dateCaptured": "2024-08-21T07:32:47.615Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2421-09328",
        "name": "Koketso",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0747480567",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Great care from all the nurses "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Best service thus far of doctors "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Cleaniness is next to godliness "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Awesome food no complains"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "",
        "surname": "Poo",
        "dateCaptured": "2024-07-18T07:21:55.624Z",
        "ward": "General Ward",
        "comment": "Tv channels ",
        "feedback": "yes",
        "id": "2418-09216",
        "name": "Titela",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0607654063",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 81.7,
        "emailAddress": "yespersey@gmail.com",
        "surname": "Mashale",
        "dateCaptured": "2024-09-18T10:52:46.238Z",
        "ward": "NICU",
        "feedback": "no",
        "id": "2418-12523",
        "name": "Perseverence",
        "reference": "Search engine (e.g., Google or Bing)"
      },
      {
        "cellphonenumber": "0835110279",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "mametsepoopedi5@gmail.com",
        "surname": "Poopedi",
        "dateCaptured": "2024-08-28T09:38:55.581Z",
        "ward": "Surgical B",
        "feedback": "no",
        "id": "2428-11389",
        "name": "Rosinah",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0791091096",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "not all of them"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "well explained "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "bedding not changed everday"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 79.1,
        "emailAddress": "Lesegominah2016@gmail.com",
        "surname": "Rankapole ",
        "dateCaptured": "2024-06-06T10:02:54.065Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2406-12022",
        "name": "Lesego",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0822970149",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 98.3,
        "emailAddress": "",
        "surname": "Nkosi",
        "dateCaptured": "2024-09-09T10:28:32.660Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2409-12288",
        "name": "Tebo",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0799029213",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Very professional and helpful staff\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Very caring and attentive "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good 👍"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "I enjoyed my stay felt like I was home"
          }
        ],
        "overallScore": 100,
        "emailAddress": "aobokwemathaba8@gmail.com",
        "surname": "Mathaba",
        "dateCaptured": "2024-05-29T12:04:07.597Z",
        "ward": "Paeds",
        "comment": "The only thing I will recommend is to be more creative with the kid's menu",
        "feedback": "yes",
        "id": "2429-14046",
        "name": "Aobokwe",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0731915786",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The admin process was done with ease and the registration part sorted with ease"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Very clean and continuous routine follow up "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 98.3,
        "emailAddress": "moipone702@gmail.com",
        "surname": "Mbiza",
        "dateCaptured": "2024-08-19T13:43:24.462Z",
        "ward": "Maternity",
        "feedback": "yes",
        "id": "2419-15434",
        "name": "Moipone",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0730755388",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Excellent service"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Excellent service"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "He even sits down to explain the scans step by step"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "I saw them the whole Day countless times "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Very warm reception "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Most definitely "
          }
        ],
        "overallScore": 91.3,
        "emailAddress": "cthabo032@gmail.com",
        "surname": "Chauke ",
        "dateCaptured": "2024-09-11T06:23:27.746Z",
        "ward": "General Ward",
        "comment": "Thank to Bra Chris n his staff and thank you so much for highcare staff night and day you have a bunch of professionals dedicated to do their work ",
        "feedback": "no",
        "id": "2411-08235",
        "name": "Thabo",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0766406069",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "None"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "None"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "To improve time it is better the Dr to come earlier than later "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "To clean locker they don't touch locker "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 74.8,
        "emailAddress": "",
        "surname": "Zulu",
        "dateCaptured": "2024-06-26T13:18:03.260Z",
        "ward": "General Ward",
        "feedback": "no",
        "id": "2426-15183",
        "name": "Gabisile",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0824154345",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Everything was perfect "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Everything was perfect "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Everything was perfect "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Every places is very clean"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Their food and service is good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "It is nice and perfect "
          }
        ],
        "overallScore": 100,
        "emailAddress": "mathapelolethabo7@gmail.com",
        "surname": "Baloyi",
        "dateCaptured": "2024-08-28T11:09:01.863Z",
        "ward": "Paeds",
        "comment": "About wife they refused to give me password because my phone got damaged and I tried to explain and show them prove but still is no from them. Now I have to buy data from my pocket wyl there is free wife at the hospital ",
        "feedback": "yes",
        "id": "2428-13098",
        "name": "Djakandjaka",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0795861068",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good"
          }
        ],
        "overallScore": 100,
        "emailAddress": "n/a",
        "surname": "Sewapa",
        "dateCaptured": "2024-05-31T12:22:59.753Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2431-14228",
        "name": "Rakgadi",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0788537101",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Perfect service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Job well-done "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Room was clean"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Food was good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good service good service "
          }
        ],
        "overallScore": 95.7,
        "emailAddress": "leratothupane@gmail.com",
        "surname": "Thupane",
        "dateCaptured": "2024-06-29T10:49:44.599Z",
        "ward": "Paeds",
        "comment": "Nope",
        "feedback": "yes",
        "id": "2429-12494",
        "name": "Lerato",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0711773823",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I am happy to be at Botshilu Hospital "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Botshilu Hospital is the best "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "I am happy "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Everything is good 👍 "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good 👍 "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good 👍 "
          }
        ],
        "overallScore": 100,
        "emailAddress": "Petermalatsi10@gmail.com",
        "surname": "Malatsi ",
        "dateCaptured": "2024-08-20T14:48:13.947Z",
        "ward": "General Ward",
        "feedback": "no",
        "id": "2420-16486",
        "name": "Peter"
      },
      {
        "cellphonenumber": "0828620141",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 92.2,
        "emailAddress": "lemaokgoadi@gmail.com",
        "surname": "Lemao",
        "dateCaptured": "2024-08-12T12:48:08.097Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2412-14481",
        "name": "Nico",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0823321391",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Everyone is friendly n can communicate with us"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Told me every step of medication"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "She explained very well for me to understand everything "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Cleaned 4times a day"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "They will always tell u they forget the Maneu n tell u they will cum back n tell u"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Everything is perfect n the staff is good "
          }
        ],
        "overallScore": 82.6,
        "emailAddress": "kutlwanobk3@gmail.com",
        "surname": "Mphela",
        "dateCaptured": "2024-06-12T11:09:33.986Z",
        "ward": "Paeds",
        "comment": "No I don't have anything course everything was good",
        "feedback": "yes",
        "id": "2412-13096",
        "name": "Lovedenia",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0646084752",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "kelebogilewhitney@gmail.com",
        "surname": "Moseja",
        "dateCaptured": "2024-09-05T12:35:12.882Z",
        "ward": "Maternity",
        "feedback": "yes",
        "id": "2405-14358",
        "name": "Kelebogile",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0717625159",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "None"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "The place it's very clean"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Food was healthy "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "My stay was good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "The staff is very friendly "
          }
        ],
        "overallScore": 100,
        "emailAddress": "vuyo.ndlovu1@gmail.com",
        "surname": "Ndlovu",
        "dateCaptured": "2024-09-06T09:31:26.572Z",
        "ward": "General Ward",
        "comment": "No",
        "feedback": "yes",
        "id": "2406-11317",
        "name": "Vuyelwa",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0648528479",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Admission process was fast and satisfying for me"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Everything was good with the meals and also the caterers where friendly and explained the menu well"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "marthamanyane3@gmail.com general",
        "surname": "Manyane ",
        "dateCaptured": "2024-07-28T07:24:28.807Z",
        "ward": "General Ward",
        "comment": "Thank you very much I had a lovely stay at the hospital ",
        "feedback": "yes",
        "id": "2428-09247",
        "name": "Martha ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "076227362",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "No comment "
          }
        ],
        "overallScore": 83.5,
        "emailAddress": "sibongilelebopa@gmail.com",
        "surname": "Shakwane",
        "dateCaptured": "2024-06-27T08:50:35.552Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2427-10508",
        "name": "Thandi",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0762236099",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93,
        "emailAddress": "moruleg3@gmail.com",
        "surname": "Morule",
        "dateCaptured": "2024-08-06T08:48:43.167Z",
        "ward": "Surgical A",
        "feedback": "no",
        "id": "2406-10484",
        "name": "Goitsimang "
      },
      {
        "cellphonenumber": "0724185298",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "All was well"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "No comments, all was ok"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good"
          }
        ],
        "overallScore": 100,
        "emailAddress": "deekgwele@gmail.com",
        "surname": "Kgwele",
        "dateCaptured": "2024-08-28T10:57:46.953Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2428-12573",
        "name": "Diana",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0681196903",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 96.5,
        "emailAddress": "kelebogilelizah22@gmail.com",
        "surname": "Mamabolo",
        "dateCaptured": "2024-09-06T06:44:56.905Z",
        "ward": "Maternity",
        "feedback": "no",
        "id": "2406-08449",
        "name": "Kelebogile",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0727391911",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Registration process at the reception takes long, then Once authorized by medical aid we still have to wait again to be taken to the ward."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Some nurses do explain the medication that they are giving the child at that time. Some nurses don't , they just come with the medication and give the child. None of the nurses explained the side effects "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "The Dr will not say much untill you ask her. Like the results of all test they took.i had to ask her what are the results of all the tests all the time."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "They do not cater enough options for kids who are on soft food/ porridge. only the mornings will they offer cooked porridge. It will be better if we can have it for lunch and super too. Not purity or cerelec for the rest of the day."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "The issue of  parent who sleeps with their kids not being served food it not ayoba/ good at all. For breast feeding mom, we need good food to nourish the breast milk to give our kids faster recovery. Now we have to depend on fast food which does not give the good nutrition to the breast milk, and contribute to the delay of the child recovery.\nSleeping couches are not comfortable to sit on for a long time.\nThere is not enough chairs for visitors or even us parents who are tired of sitting on this sleeping couches."
          }
        ],
        "overallScore": 73,
        "emailAddress": "annah.matlou@nokia.com",
        "surname": "Matlou",
        "dateCaptured": "2024-07-18T08:58:50.680Z",
        "ward": "Paeds",
        "comment": "Give parents who sleeps with their kids at the ward free food. The one we pay for is even small.\nHave enough food at the cafe. Like full loave of bread. \nCafe should close later at least at 9 0r 10 pm\nVending mechines do not have prices on them. Put on the prices.\n\n\n",
        "feedback": "yes",
        "id": "2418-10588",
        "name": "Nthabiseng",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0813220865",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Please clean clean the hospital. "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Butshilu hospital is losing the value of being private"
          }
        ],
        "overallScore": 47.8,
        "emailAddress": "zinaona17@gmail.com",
        "surname": "Mhlanga ",
        "dateCaptured": "2024-09-14T05:48:53.298Z",
        "ward": "Maternity",
        "comment": "No) ",
        "feedback": "no",
        "id": "2414-07488",
        "name": "Onalenna ",
        "reference": "Search engine (e.g., Google or Bing)"
      },
      {
        "cellphonenumber": "0609554402",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I am happy with the entire service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 95.7,
        "emailAddress": "sophymasemedi@gmail.com",
        "surname": "Masemedi",
        "dateCaptured": "2024-05-31T11:23:28.914Z",
        "ward": "Maternity",
        "feedback": "yes",
        "id": "2431-13237",
        "name": "Sophy",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0844330278",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 82.6,
        "emailAddress": "",
        "surname": "Moema",
        "dateCaptured": "2024-07-05T12:23:59.581Z",
        "ward": "Maternity",
        "feedback": "yes",
        "id": "2405-14239",
        "name": "Boitumelo",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0732031304",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 86.1,
        "emailAddress": "bmorige53@gmail.com",
        "surname": "Morige ",
        "dateCaptured": "2024-09-12T05:50:39.642Z",
        "ward": "General Ward",
        "feedback": "no",
        "id": "2412-07507",
        "name": "Boipelo ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0795465285",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Keep up the good work"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 95.7,
        "emailAddress": "lehlohonolonthite@gmail.com",
        "surname": "Nthite",
        "dateCaptured": "2024-08-21T10:46:22.012Z",
        "ward": "Paeds",
        "comment": "Keep up the good work",
        "feedback": "yes",
        "id": "2421-12464",
        "name": "Lehlohonolo",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0731915786",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Informed me of all rules"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Concerns were cleared "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "She even took upon herself to get me extra knowledge and support "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Proper sanitizing and hand cleaning was frequently practiced "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Baby takes breastfeeding milk"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "moipone702@gmail.com",
        "surname": "Mbiza",
        "dateCaptured": "2024-08-23T11:01:48.595Z",
        "ward": "NICU",
        "comment": "",
        "feedback": "yes",
        "id": "2423-13016",
        "name": "Moipone",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0694028715",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 90.4,
        "emailAddress": "lista0701@icloud.com",
        "surname": "Kekana",
        "dateCaptured": "2024-09-11T11:01:05.490Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2411-13019",
        "name": "Thando ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0723429981",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "khumotr@yahoo.com",
        "surname": "Mashabela",
        "dateCaptured": "2024-08-27T08:50:40.418Z",
        "ward": "Surgical B",
        "comment": "The stay was pleasant ",
        "feedback": "yes",
        "id": "2427-10505",
        "name": "Noxolo",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0769639398",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 95.7,
        "emailAddress": "vanny.nhlapo@gmail.com",
        "surname": "Nhlapo",
        "dateCaptured": "2024-05-29T12:19:55.033Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2429-14192",
        "name": "Mahlatse",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0781498768",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "kimsodi755@gmail.com",
        "surname": "Sodi",
        "dateCaptured": "2024-07-17T12:24:58.742Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2417-14245",
        "name": "Kim",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0729446362",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Due to my medical aid I was almost send back with the child who was very sick...I was supposed to pay cash the doctor didn't even address me well ..she was like if I don't have money they can't help me instead of explaining to me .."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 86.1,
        "emailAddress": "rushella38@gmail.com",
        "surname": "Mophuting",
        "dateCaptured": "2024-09-18T12:11:50.791Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2418-14118",
        "name": "Rushella",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0712814292",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 50.4,
        "emailAddress": "kamomotubatse649@gmail.com",
        "surname": "Motubatse",
        "dateCaptured": "2024-08-07T07:41:03.657Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2407-09412",
        "name": "Kamogelo",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0823100805",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93,
        "emailAddress": "lesegoemily21@gmail.com",
        "surname": "Chula",
        "dateCaptured": "2024-09-17T07:34:47.734Z",
        "ward": "General Ward",
        "comment": "No",
        "feedback": "yes",
        "id": "2417-09349",
        "name": "Lesego",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0676968732",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "No comment "
          }
        ],
        "overallScore": 94.8,
        "emailAddress": "",
        "surname": "Makhubele5",
        "dateCaptured": "2024-08-21T13:29:19.790Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2421-15298",
        "name": "Esther",
        "reference": "Facebook"
      },
      {
        "cellphonenumber": "0725585968",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Molebogeng made my stay easy and she was so friendly to approach."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 84.3,
        "emailAddress": "lebo.matsie@gmail.com",
        "surname": "Khumalo ",
        "dateCaptured": "2024-08-15T07:25:28.784Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2415-09256",
        "name": "Lebogang ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0636053456",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Goood"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good"
          }
        ],
        "overallScore": 96.5,
        "emailAddress": "matlhodikadiaka@gmail.com",
        "surname": "Kadiaka",
        "dateCaptured": "2024-08-30T10:57:11.962Z",
        "ward": "Maternity",
        "comment": "No",
        "feedback": "yes",
        "id": "2430-12573",
        "name": "Matlhodi",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0637034132",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Happy"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Happy 😃"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 96.5,
        "emailAddress": "",
        "surname": "Maponya",
        "dateCaptured": "2024-08-23T12:50:34.934Z",
        "ward": "Surgical A",
        "feedback": "yes",
        "id": "2423-14508",
        "name": "Jackson ",
        "reference": "Facebook"
      },
      {
        "cellphonenumber": "0734174637",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Good service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Satisfied "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good se"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good service "
          }
        ],
        "overallScore": 100,
        "emailAddress": "vashney056@gmail.co.za",
        "surname": "Mahoshi",
        "dateCaptured": "2024-08-23T10:31:06.872Z",
        "ward": "Surgical B",
        "comment": "So far everything was ok ",
        "feedback": "yes",
        "id": "2423-12314",
        "name": "Monnye",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0764419134",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Their meals are always on point "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "They must try to minimize noise level"
          }
        ],
        "overallScore": 88.7,
        "emailAddress": "na",
        "surname": "Molwantwa",
        "dateCaptured": "2024-06-26T14:17:47.717Z",
        "ward": "General Ward",
        "id": "2426-16171",
        "name": "Tshireletso",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0791173919",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The admission process was fast."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 90.4,
        "emailAddress": "forgivenonhlanhla14@gmail.com",
        "surname": "Khoza",
        "dateCaptured": "2024-06-12T08:51:40.383Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2412-10516",
        "name": "Forgive",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0849770660",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93,
        "emailAddress": "thabilesibande41@gmail.com",
        "surname": "Sibande",
        "dateCaptured": "2024-08-28T11:29:54.153Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2428-13292",
        "name": "Thabile",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0728593452",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "They must clean offen"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 88.7,
        "emailAddress": "kaymabye@gmail.com",
        "surname": "Mabye",
        "dateCaptured": "2024-08-21T13:33:12.775Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2421-15339",
        "name": "Koketso ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0724671698",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93,
        "emailAddress": "kamogelomhlanga94@gmail.com",
        "surname": "Mhlanga",
        "dateCaptured": "2024-06-27T09:34:26.710Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2427-11342",
        "name": "Kamogelo",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0670787612",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 95.7,
        "emailAddress": "amokoola96@gmail.com",
        "surname": "Koola",
        "dateCaptured": "2024-08-12T12:36:37.033Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2412-14367",
        "name": "Amogelang",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0723604886",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "The only nurse who gave excellent service is Gugu unfortunately I can't rate them individually "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Menu was not entirely accommodative "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 68.7,
        "emailAddress": "kgomotsoevamoselana@gmail.com",
        "surname": "Moselana",
        "dateCaptured": "2024-05-30T13:50:00.376Z",
        "ward": "Maternity",
        "comment": "I am not happy with service provided. I am not coming back to this place\n",
        "feedback": "no",
        "id": "2430-15504",
        "name": "Kgomotso",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0638447308",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 88.7,
        "emailAddress": "badanilesharon39@gmail.com",
        "surname": "Phiri",
        "dateCaptured": "2024-08-15T12:26:21.209Z",
        "ward": "Paeds",
        "comment": "Perfect ",
        "feedback": "yes",
        "id": "2415-14263",
        "name": "Sharon",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0781016888",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Sister who assisted was friendly and patient with us."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Nurses were very informative and explaining everything they were doing to my child.\nVery patient and friendly staff.\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Yes she did.She went extra mile to help me with my son's condition ,wellbeing and how to look after my baby "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Very unhappy with the bathrooms.Most of the time during my stay the toilet was not cleaned and the bathtub.\n\nIf we can clean for ourselves, they can just put soaps,buckets and cloths for us,"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "No comment"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Butshilu is still my go to hospital."
          }
        ],
        "overallScore": 88.7,
        "emailAddress": "Marg.Mashiane@absa.africa",
        "surname": "Mashiane ",
        "dateCaptured": "2024-09-02T11:03:11.849Z",
        "ward": "Paeds",
        "comment": "None",
        "feedback": "yes",
        "id": "2402-13032",
        "name": "Margaret Ntombi",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0760378007",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "mukhethwamakhari@gmail.com",
        "surname": "Makhari",
        "dateCaptured": "2024-08-25T11:00:20.917Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2425-13undefined1",
        "name": "Faith",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0699504330",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 94.8,
        "emailAddress": "t7612247@gmail.com",
        "surname": "Legoabe ",
        "dateCaptured": "2024-08-09T06:43:57.227Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2409-08433",
        "name": "Tshepiso ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0782434680",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "barbaramabogoane93@mail.com",
        "surname": "Mabogoane",
        "dateCaptured": "2024-09-06T11:38:22.161Z",
        "ward": "Surgical B",
        "feedback": "no",
        "id": "2406-13384",
        "name": "Tebogo ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0843147127",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "Refilweruth400@gmail.com",
        "surname": "Phaka",
        "dateCaptured": "2024-06-06T13:34:04.182Z",
        "ward": "Maternity",
        "comment": "No. Was very satisfied ",
        "feedback": "yes",
        "id": "2406-15347",
        "name": "Refilwe",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0761493469",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Everything went well"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Everything was explained to me "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Good service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "It was clean all the time"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "cynthiakgomotso@gmail.com",
        "surname": "Sibeko",
        "dateCaptured": "2024-07-05T08:57:10.220Z",
        "ward": "Maternity",
        "comment": "Everything was in good order no complains whatsoever",
        "feedback": "no",
        "id": "2405-10573",
        "name": "Cynthia",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0658691666",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Excellent "
          }
        ],
        "overallScore": 97.4,
        "emailAddress": "poppy.tshishonge@dcs.gov.za",
        "surname": "Tshishonge ",
        "dateCaptured": "2024-09-07T07:31:17.058Z",
        "ward": "General Ward",
        "comment": "More staff",
        "feedback": "yes",
        "id": "2407-09317",
        "name": "Charlotte poppy ",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0622088748",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Very good "
          }
        ],
        "overallScore": 100,
        "emailAddress": "",
        "surname": "Skhosana",
        "dateCaptured": "2024-06-27T11:25:02.976Z",
        "ward": "Surgical B",
        "comment": "No complain ",
        "feedback": "yes",
        "id": "2427-13255",
        "name": "Orphelia",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0791071382",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I got greater love n care"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "The nurses serve true love and 😘"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Great doctor "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Excellent meal "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Great hospital o"
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "486 block x",
        "surname": "Mongwe",
        "dateCaptured": "2024-08-23T11:37:26.351Z",
        "ward": "Maternity",
        "comment": "Keep up the good work n keep treating other paitents like me 😘😘",
        "feedback": "yes",
        "id": "2423-13379",
        "name": "Lerato",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0697710017",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "nanamokhele@gmail.com",
        "surname": "Mokhele",
        "dateCaptured": "2024-08-12T12:53:09.618Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2412-14538",
        "name": "Nana",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0731233018",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Excellent service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Excellent service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Excellent service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "The toilet paper is not comfortable to use is scratchy rough"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Excellent service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Excellent service "
          }
        ],
        "overallScore": 94.8,
        "emailAddress": "lindyy.skhosy@gmail.com",
        "surname": "Skhosana",
        "dateCaptured": "2024-06-22T12:56:24.462Z",
        "ward": "Maternity",
        "comment": "Excellent service",
        "feedback": "yes",
        "id": "2422-14567",
        "name": "Lindiwe",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0728306951",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "They welcome me with a smiles"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "I am satisfied by the nurses when giving medications(they are using 5 golden rules )"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Happy about the friendly of the nurses"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "The basin at the showering room must be in good order( leaking)"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "If they are not feeling well,pregnant must come and consulting at Botshilu Private hospital  meternity ward"
          }
        ],
        "overallScore": 84.3,
        "emailAddress": "zipporahtshabangu@gmail.za",
        "surname": " Tshabangu",
        "dateCaptured": "2024-08-30T11:39:27.999Z",
        "ward": "Maternity",
        "comment": "Clean frequently ",
        "feedback": "yes",
        "id": "2430-13392",
        "name": "Mmanakedi Zipporah",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0711950378",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Staff was good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Frendly"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Happy patient"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Room was clean"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Dinner menue not nice"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Definetly"
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "kgmasales@gmail.com",
        "surname": "Masalesa",
        "dateCaptured": "2024-08-22T10:49:39.425Z",
        "ward": "Surgical B",
        "comment": "Happy",
        "feedback": "no",
        "id": "2422-12496",
        "name": "Kgomotso",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0722707831",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "chamanemore@gmail.com",
        "surname": "More",
        "dateCaptured": "2024-09-18T12:21:15.069Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2418-14215",
        "name": "Solomon",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0817397172",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "",
        "surname": "Mahlangu",
        "dateCaptured": "2024-09-12T08:15:15.005Z",
        "ward": "General Ward",
        "comment": "Keep up the good work ",
        "feedback": "yes",
        "id": "2412-10155",
        "name": "Success ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0812081818",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 80,
        "emailAddress": "mkhwanazimercia@gmail.com",
        "surname": "Mkhwanazi ",
        "dateCaptured": "2024-06-14T07:34:40.272Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2414-09343",
        "name": "Mercia ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0846936387",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "No comments "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Excellent staff"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Nothing "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "The kitchen staff must clear the tables in time after every meal"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Staff to keep up the good work "
          }
        ],
        "overallScore": 96.5,
        "emailAddress": "georgendhlovu@yahoo.com",
        "surname": "Ndhlovu",
        "dateCaptured": "2024-05-31T12:52:46.017Z",
        "ward": "General Ward",
        "comment": "Your doctor's must introduce themselves to the patients they are treating.",
        "feedback": "yes",
        "id": "2431-14523",
        "name": "George",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0605540254",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 85.2,
        "emailAddress": "louisalinken@gmail.com",
        "surname": "Linken",
        "dateCaptured": "2024-09-05T07:30:41.382Z",
        "ward": "Surgical B",
        "feedback": "no",
        "id": "2405-09303",
        "name": "Louisa",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0790510154",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The staff is very helpful and friendly.😀"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 97.4,
        "emailAddress": "nyathikazithuli1@gmail.com",
        "surname": "Nyathikazi",
        "dateCaptured": "2024-07-08T13:14:21.616Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2408-15144",
        "name": "Thulisiwe",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0763134757",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Good stuff and friendly "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Improve in communicate "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "They tried in good care"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "The ward is clean "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good food "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Clean everything "
          }
        ],
        "overallScore": 93,
        "emailAddress": "tshegoidah2@gmail",
        "surname": "Mfete",
        "dateCaptured": "2024-07-11T11:17:59.280Z",
        "ward": "Paeds",
        "comment": "No",
        "feedback": "yes",
        "id": "2411-13173",
        "name": "Tshegofatso",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0786921394",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Everything was explained to our understanding "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "The nurses were very friendly and courteous with us"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Dr Mchenga is the best at her job"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Everything was spotless "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "The food was delicious "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "I appreciated the service "
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "dmabusela068@gmail.com",
        "surname": "Mahlangu",
        "dateCaptured": "2024-06-19T14:30:07.285Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2419-16305",
        "name": "Dikeledi",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0762472753",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "The nurse staff made my stay welcoming and always friendly "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Thank to Dr MAKGOKA AND EVER SMILLING DR NHLATSWAYO"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "owakho@gmail.com",
        "surname": "Masombuka",
        "dateCaptured": "2024-08-29T11:01:49.801Z",
        "ward": "Surgical B",
        "comment": "I will recommend this hospital to family and friends",
        "feedback": "yes",
        "id": "2429-13012",
        "name": "Alice",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0767027731",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Service was exeptional"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Job well done for the friendly staff"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "I was satisfied with the treatment"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Many times"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Food was well prepared with love"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Highly recommended"
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "cindisilwen@gmail.com",
        "surname": "Ngobeni",
        "dateCaptured": "2024-09-06T08:54:27.925Z",
        "ward": "Paeds",
        "comment": "Keep up the good work",
        "feedback": "yes",
        "id": "2406-10545",
        "name": "Cindisilwe",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0766155599",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 83.5,
        "emailAddress": "ThibelaS@saps.gov.za",
        "surname": "Thibela",
        "dateCaptured": "2024-06-27T08:50:25.505Z",
        "ward": "General Ward",
        "comment": "Patiànts is 5he first priority àt the Health Care facilities, when admitted their diet must be taken into consideration for their best recovery. Patiànts ends up been in hurry to be discharge due to things likes menu within given doesn't benefit to their reçovery. Ward matters you cannot place woman facing the beds of men you uncomfortable of which it is not conducive for some of the patients.",
        "feedback": "yes",
        "id": "2427-10507",
        "name": "Shirley  nkeli",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0698930777",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 98.3,
        "emailAddress": "sicelonkambule551@gmail.com",
        "surname": "Nkambule ",
        "dateCaptured": "2024-08-26T10:54:40.633Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2426-12543",
        "name": "sicelo",
        "reference": "Search engine (e.g., Google or Bing)"
      },
      {
        "cellphonenumber": "0787703143",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I loved the service and they took care of my baby "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 98.3,
        "emailAddress": "manakomothebe@gmail.com",
        "surname": "Mothebe",
        "dateCaptured": "2024-07-26T08:51:40.374Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2426-10518",
        "name": "Manako",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0769500483",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Happy"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Happy"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Happy"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Happy"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Happy"
          }
        ],
        "overallScore": 100,
        "emailAddress": "geophreymotaung@gmail.com",
        "surname": "Motaung",
        "dateCaptured": "2024-09-18T10:30:14.287Z",
        "ward": "Surgical B",
        "comment": "Happy patient",
        "feedback": "yes",
        "id": "2418-12308",
        "name": "Geophrey",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0743817171",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The stuff was super nice and compassionate."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Very informative about my condition and explain better way to care for myself."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "They were friendly."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Food was delicious and tasty."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "My family was impressed at the service I got. We are very glad I came here."
          }
        ],
        "overallScore": 100,
        "emailAddress": "kgomotsoqsithole.ks@gmail.com",
        "surname": "Maseko",
        "dateCaptured": "2024-06-02T09:16:28.233Z",
        "ward": "General Ward",
        "comment": "The service and care is exceptional. KEEP IT UP!",
        "feedback": "yes",
        "id": "2402-11163",
        "name": "Kgomotso",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0724268317",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 95.7,
        "emailAddress": "",
        "surname": "Masombuka",
        "dateCaptured": "2024-06-25T06:59:42.571Z",
        "ward": "Paeds",
        "comment": "No everything is good ",
        "feedback": "yes",
        "id": "2425-08598",
        "name": "Winnie ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0818099176",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I  waited for more than 2 hours before they can admit my son and he was not in a good condition"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "She was just doing her job without explaining sometimes I would ask questions as a concern mother \nNonetheless she's a good loving nurse she just have to inform parents about the medication she's offering the child so that we can be informative\n\n\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "My kiddie's meal was not bad at all but mine(mother)was not good m not impressed about it especially the breakfast"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 68.7,
        "emailAddress": "samkeleross@gmail.com",
        "surname": "Makhubela",
        "dateCaptured": "2024-06-18T09:57:02.000Z",
        "ward": "Paeds",
        "comment": "The attitude of one of ur cleaners  other than that everything is good and compliments goes to ur nurses ",
        "feedback": "yes",
        "id": "2418-11571",
        "name": "Marriam",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0836510827",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Everything was good except for the kitchen ....food was not okay"
          }
        ],
        "overallScore": 83.5,
        "emailAddress": "",
        "surname": "Mateche",
        "dateCaptured": "2024-09-03T11:33:06.445Z",
        "ward": "Surgical A",
        "feedback": "yes",
        "id": "2403-13334",
        "name": "Mateche",
        "reference": "Facebook"
      },
      {
        "cellphonenumber": "0788004965",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Side effects if any were not explained "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Patients gear left lying around in shower rooms \nCan you please have bins with lids in bathrooms we don't want to see the contents"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "All was just fine except choices are limited "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 85.2,
        "emailAddress": "mashambab@gmail.com",
        "surname": "Mashamba ",
        "dateCaptured": "2024-06-02T09:39:55.303Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2402-11394",
        "name": "Boitumelo",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0840398418",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 77.4,
        "emailAddress": "selina.mosobe@phelangbonolo.co.za",
        "surname": "Mosobe",
        "dateCaptured": "2024-06-04T13:03:45.097Z",
        "ward": "ICU",
        "feedback": "yes",
        "id": "2404-15033",
        "name": "Selina",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0645178356",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I like the treatment from sr mashigo and Ayanda was excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "They must make sure they clean the bathroom and showers regularly"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Excellent "
          }
        ],
        "overallScore": 89.6,
        "emailAddress": "mariachokwe74@gmail.com",
        "surname": "Chokwe",
        "dateCaptured": "2024-09-06T10:57:20.383Z",
        "ward": "General Ward",
        "comment": "Cleaning services must improvement ",
        "feedback": "yes",
        "id": "2406-12578",
        "name": "Maria",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0720564779",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 87.8,
        "emailAddress": "gkoza2034@gemail,com",
        "surname": "Koza",
        "dateCaptured": "2024-08-19T14:41:00.959Z",
        "ward": "Surgical A",
        "comment": "No thanks ",
        "feedback": "yes",
        "id": "2419-16411",
        "name": "Godfrey",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0791820807",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 97.4,
        "emailAddress": "",
        "surname": "Motau ",
        "dateCaptured": "2024-09-12T14:20:10.475Z",
        "ward": "Maternity",
        "feedback": "yes",
        "id": "2412-16202",
        "name": "Mokgadi",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0723129911",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 97.4,
        "emailAddress": "",
        "surname": "Rakwena",
        "dateCaptured": "2024-09-08T09:17:30.362Z",
        "ward": "Surgical B",
        "comment": "Keep up the good work thank you for good service",
        "feedback": "yes",
        "id": "2408-11174",
        "name": "Solomon",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0637087574",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "",
        "surname": "Manuel",
        "dateCaptured": "2024-07-24T09:28:04.968Z",
        "ward": "General Ward",
        "feedback": "no",
        "id": "2424-11288",
        "name": "Leroy",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0711330626",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "N/A"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Other nurses are friendly, others are fine and so far ever since I have been admitted I have had a bad experience with one nurse, for the status and the reputation of the hospital I do not recommend that nurse"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "N/A"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "N/A"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Eggs made for the kids are not seasoned at all they're bland and my son would not want to eat them"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "The noise and disturbance at night, opening the doors of the patients rooms and leaving them opened, banging of the doors and bins during the night would wake my son up from his sleep. "
          }
        ],
        "overallScore": 68.7,
        "emailAddress": "refiloethandow@gmail.com",
        "surname": "Mabena",
        "dateCaptured": "2024-08-28T10:24:22.614Z",
        "ward": "Paeds",
        "comment": "My son is on drip and because he's a baby he keeps fighting and trying to get the drip off him and this will cause the drip machine to beep due to an occlusion. I'll ring the bell for assistance now and then because this is a baby, they come and fix and when my attempts to stop him from pulling the drip and fighting fails the machin beeps again and I have to call the nurses for assistance and some would now get impatient when they come and assist and even take time to come. Isn't there any way to make these drip machines not this difficult so that the nurses don't make us feel like we're bothering them? ",
        "feedback": "yes",
        "id": "2428-12248",
        "name": "Noluthando",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0612650410",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 66.1,
        "emailAddress": "orateledimo@gmail.com",
        "surname": "Teledimo",
        "dateCaptured": "2024-06-18T07:36:00.057Z",
        "ward": "General Ward",
        "feedback": "no",
        "id": "2418-09369",
        "name": "Oratile",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0794567387",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Salt intake please reduce"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Excellent "
          }
        ],
        "overallScore": 100,
        "emailAddress": "tjmahlaola",
        "surname": "Mahlaola",
        "dateCaptured": "2024-09-11T11:03:35.178Z",
        "ward": "General Ward",
        "comment": "Non",
        "feedback": "yes",
        "id": "2411-13034",
        "name": "Tshidi",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0794008036",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 96.5,
        "emailAddress": "",
        "surname": "Malatji",
        "dateCaptured": "2024-06-22T10:06:09.591Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2422-12061",
        "name": "Tebogo ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0713338322",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "We are not at the public hospital, we expect excellency, at the admission. I brought a sick child and waited too long to be admitted\n\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 55.7,
        "emailAddress": "margaretk@pprotect.org",
        "surname": "Kowane",
        "dateCaptured": "2024-06-12T11:29:42.502Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2412-13295",
        "name": "Maggie",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0729438848",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "No potential side effects were explained even though administration was explained\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93,
        "emailAddress": "malulekamahlatse99@gmail.com",
        "surname": "Maluleka ",
        "dateCaptured": "2024-07-24T07:40:01.161Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2424-09401",
        "name": "Mahlatse ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0814147738",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "makhubedud@yahoo.com",
        "surname": "Bodigelo",
        "dateCaptured": "2024-09-10T08:38:37.089Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2410-10383",
        "name": "Daphney",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0647755167",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Very was good\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 84.3,
        "emailAddress": "mpumelelompumi354@gmail.com",
        "surname": "Magagula",
        "dateCaptured": "2024-08-12T14:16:41.696Z",
        "ward": "Paeds",
        "comment": "No",
        "feedback": "yes",
        "id": "2412-16164",
        "name": "Mpumelelo",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0720407376",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "karabeloruth@gmail.com",
        "surname": "Msiza",
        "dateCaptured": "2024-06-14T09:12:05.102Z",
        "ward": "Paeds",
        "comment": "All is well, keep up the excellent service.",
        "feedback": "no",
        "id": "2414-11124",
        "name": "Karabelo",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0665131360",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Everything is perfect no complaint raised "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Medication help to recover so quickly "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "The Doctor was friendly "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Everything is perfect "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "The food was nice"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Botshilo private hospital is the best place ever"
          }
        ],
        "overallScore": 100,
        "emailAddress": "Melitammaserame89@gmail.com",
        "surname": "Khoza ",
        "dateCaptured": "2024-09-11T06:52:22.888Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2411-08525",
        "name": "Melita ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0797188528",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I am satisfied with the level of professionalism and care."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Nursing staff is caring, patient and highly professional"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Great doctors 😍"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93,
        "emailAddress": "moetsi.kganyago@gmail.com",
        "surname": "Kganyago",
        "dateCaptured": "2024-06-21T13:48:27.304Z",
        "ward": "Surgical A",
        "comment": "The only set back was that the toilet in the emergency/trauma ward was out of order at the time of our admission😌😌😌",
        "feedback": "yes",
        "id": "2421-15485",
        "name": "Tshepo",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0833154071",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 66.1,
        "emailAddress": "NS.Sealetsa@gmail.com",
        "surname": "Sealetsa",
        "dateCaptured": "2024-07-24T11:49:09.543Z",
        "ward": "Paeds",
        "comment": "I would advise\n1.  the staff to elaborate on the medical contion of the patient and the medication administered.\n2. Have room for parent opinion\n3. Effeciency of ward staff",
        "feedback": "yes",
        "id": "2424-13493",
        "name": "Neo",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0728667439",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 98.3,
        "emailAddress": "pd.phoks@gmail.com",
        "surname": "Phokungoane",
        "dateCaptured": "2024-08-21T06:51:31.686Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2421-08518",
        "name": "Pheladi",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0817076356",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "It was very quick "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Yes they explain very well"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Yes she did in a simpler terms🤣"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Cleaners are very friendly they can make you feel home"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "They explained the menu so that you don't get suprised"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "So far their number one on my list🥰"
          }
        ],
        "overallScore": 100,
        "emailAddress": "nutriciatong14@gmail.com",
        "surname": "Tong",
        "dateCaptured": "2024-09-10T08:15:18.653Z",
        "ward": "Paeds",
        "comment": "All I can is that they must keep up the good work and continue giving the best service ever\nMay God grant them many many years to serve 💯🥰🥰",
        "feedback": "no",
        "id": "2410-10155",
        "name": "Nutricia",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0781005356",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The service was superb, thank you very much."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Am satisfied with your service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Thank you"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Superb service"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Everything was excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Botshilu is a great hospital"
          }
        ],
        "overallScore": 86.1,
        "emailAddress": "maluleke.martha@yahoo.com",
        "surname": "Maluleke ",
        "dateCaptured": "2024-08-08T08:00:00.045Z",
        "ward": "General Ward",
        "comment": "Thank you very much",
        "feedback": "yes",
        "id": "2408-10undefined4",
        "name": "Martha",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0823959772",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 95.7,
        "emailAddress": "kefiloe74@yahoo.com",
        "surname": "Modibane",
        "dateCaptured": "2024-09-11T07:54:10.711Z",
        "ward": "Surgical B",
        "feedback": "no",
        "id": "2411-09542",
        "name": "Jacobeth",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0795743532",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 88.7,
        "emailAddress": "deemochocho@gmail.com",
        "surname": "Mochocho",
        "dateCaptured": "2024-06-04T13:36:57.927Z",
        "ward": "General Ward",
        "feedback": "no",
        "id": "2404-15365",
        "name": "Dimakatso",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0768521473",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Excellent service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Great job really "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Bathroom are not clean "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Cold "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 90.4,
        "emailAddress": "evelynmaleka1@gmail.com",
        "surname": "Maleka",
        "dateCaptured": "2024-06-18T13:10:48.007Z",
        "ward": "Paeds",
        "comment": "Keep it up 👍",
        "feedback": "no",
        "id": "2418-15104",
        "name": "Evelyn ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0768090856",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "vuthlari19@gmail.com",
        "surname": "Makondo",
        "dateCaptured": "2024-08-15T10:34:12.743Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2415-12345",
        "name": "Tinyiko",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0828669457",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The treatment was exceptionally good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 98.3,
        "emailAddress": "",
        "surname": "Mohale",
        "dateCaptured": "2024-08-29T05:54:12.963Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2429-07543",
        "name": "Thembekile",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0783835961",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "nonkulumobn@gmail.com",
        "surname": "Mokoena",
        "dateCaptured": "2024-09-11T07:07:06.900Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2411-09072",
        "name": "Bawinile",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0730693095",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 89.6,
        "emailAddress": "lebuthi@gmail.com",
        "surname": "Mokgosi",
        "dateCaptured": "2024-08-21T06:40:34.322Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2421-08409",
        "name": "Mmaphuthi",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0767127381",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "The catering stuff is very friendly and the food tastes really good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "I will surely recommend your hospital to all people who need medical attention "
          }
        ],
        "overallScore": 80,
        "emailAddress": "kelebogilelegwabe96@gmail.com",
        "surname": "Legwabe ",
        "dateCaptured": "2024-08-29T08:06:35.924Z",
        "ward": "Surgical A",
        "comment": "No I don't don't I am happy with everything ",
        "feedback": "no",
        "id": "2429-10069",
        "name": "Kelebogile ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0727680468",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "",
        "surname": "Malema",
        "dateCaptured": "2024-09-10T12:05:36.957Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2410-14053",
        "name": "Elizabeth ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0767253744",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "",
        "surname": "Dekio",
        "dateCaptured": "2024-06-24T08:42:16.403Z",
        "ward": "Maternity",
        "feedback": "yes",
        "id": "2424-10427",
        "name": "Give",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0818601530",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I love that they make sure that you know all the rules.do feel that I am at home."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "So that I know what is given too my son and understand 💓"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "I even know what is the problem with my son.so that I can make sure when I am at home too do the correct thing, too take care of my son."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "But all this will happen when we talk about it."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Can it have a better taste so that it can be eatble like eg mashed 🍠,🥔."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "I will do that coz I had too be given an excellent service.if I had money I was going to give birth here."
          }
        ],
        "overallScore": 91.3,
        "emailAddress": "nomusachobam@gmail.com",
        "surname": "Choba",
        "dateCaptured": "2024-07-16T11:15:17.606Z",
        "ward": "Paeds",
        "comment": "",
        "feedback": "yes",
        "id": "2416-13154",
        "name": "Nomusa Meltha",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0678989766",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 89.6,
        "emailAddress": "kgomotsotsoku@gmail.com",
        "surname": "Tsoku",
        "dateCaptured": "2024-08-25T12:07:13.704Z",
        "ward": "Paeds",
        "comment": "No",
        "feedback": "no",
        "id": "2425-14079",
        "name": "kgomotso",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0645148546",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Services was good 😊"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "I'm happy with the service nurses were good both day and night."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Your service is very good 😊😊"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Keep up the good work and excellent service that was given😊😊"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "The food was not good especially the Rice that I ate..was half done and I had running stomach at night.Atleast try to improve "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "The hospital is near to us and also the service was very good 😊"
          }
        ],
        "overallScore": 89.6,
        "emailAddress": "",
        "surname": "Llale",
        "dateCaptured": "2024-06-21T09:27:54.248Z",
        "ward": "Surgical B",
        "comment": "The kitchen food is very 😔...But overall the nurses are very kind and friendly.. Reception perfect 😊😊\nKeep up the good work 👍",
        "feedback": "yes",
        "id": "2421-11278",
        "name": "Mmashela",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0781498768",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "kimsodi755@gmail.com",
        "surname": "Sodi",
        "dateCaptured": "2024-08-28T10:48:57.803Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2428-12484",
        "name": "Kim",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0635804062",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Am happy"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "The explainations are clear enough when there is questions being asked\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "The doctors does everything in their best which is good for our children "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Love the services "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "The children recieve meals accordingly "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "I would always keep in mind that this hospital is one of the best"
          }
        ],
        "overallScore": 100,
        "emailAddress": "didintlemosadi403@gmail.com",
        "surname": "Mosadi",
        "dateCaptured": "2024-08-27T13:41:01.689Z",
        "ward": "Paeds",
        "comment": "None",
        "feedback": "yes",
        "id": "2427-15416",
        "name": "Didintle",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0730195563",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Very good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "",
        "surname": "Mathabathe",
        "dateCaptured": "2024-09-08T08:31:49.706Z",
        "ward": "Surgical B",
        "comment": "Satisfied ",
        "feedback": "yes",
        "id": "2408-10315",
        "name": "Elisa",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0781279097",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "All the staff were great. What an excellent service thank you "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Very helpful"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Great"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Keep up the good work"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Very good hospital"
          }
        ],
        "overallScore": 100,
        "emailAddress": "dimakatsolebese@ymail.com",
        "surname": "Lebese",
        "dateCaptured": "2024-06-14T09:25:44.345Z",
        "ward": "Paeds",
        "comment": "Keep up the good work \nI really appreciate your service \n\nImpressed ",
        "feedback": "yes",
        "id": "2414-11256",
        "name": "Dimakatso",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0781498768",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "kimsodi755@gmail.com",
        "surname": "Sodi",
        "dateCaptured": "2024-07-16T11:00:36.694Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2416-13undefined9",
        "name": "Kim",
        "reference": "Search engine (e.g., Google or Bing)"
      },
      {
        "cellphonenumber": "0817079802",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 79.1,
        "emailAddress": "dineo.molapo@gauteng.gov.za",
        "surname": "Matjila",
        "dateCaptured": "2024-05-29T12:08:11.795Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2429-14086",
        "name": "Dineo",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0817462142",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Excellent"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "nkeletefo22@gmail.com",
        "surname": "Tefo",
        "dateCaptured": "2024-09-16T16:12:24.829Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2416-18122",
        "name": "Pauline",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0839589646",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "",
        "surname": "Nkosi",
        "dateCaptured": "2024-09-02T10:48:52.860Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2402-12481",
        "name": "Wonder",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0788703298",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "None"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "",
        "surname": "Mathumo",
        "dateCaptured": "2024-05-31T13:13:48.616Z",
        "ward": "Surgical B",
        "feedback": "no",
        "id": "2431-15132",
        "name": "Mancha",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0748002878",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "",
        "surname": "Sekgaolelo",
        "dateCaptured": "2024-09-03T04:45:16.490Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2403-06455",
        "name": "Lerato"
      },
      {
        "cellphonenumber": "0798681811",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "omphilepearl@gmail.com",
        "surname": "Bogoshi",
        "dateCaptured": "2024-07-11T13:27:32.731Z",
        "ward": "Paeds",
        "comment": "Provide big cups at tea stations",
        "feedback": "no",
        "id": "2411-15275",
        "name": "Omphile",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0829423347",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "sellotsomele86@gmail.com",
        "surname": "Tsomele",
        "dateCaptured": "2024-09-11T11:56:50.955Z",
        "ward": "General Ward",
        "comment": "Keep up the good work ",
        "feedback": "yes",
        "id": "2411-13568",
        "name": "Sello",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0829438440",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Excellent service keep it up 💯"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Love they nursing care thank you good service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Most info native care thank you again 🙏"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Out standing "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Excellent 👌"
          }
        ],
        "overallScore": 100,
        "emailAddress": "evelynnk.gmail.com",
        "surname": "Nkomo",
        "dateCaptured": "2024-08-28T07:17:37.216Z",
        "ward": "General Ward",
        "comment": "Thank you 🙏😊",
        "feedback": "yes",
        "id": "2428-09175",
        "name": "Evelyn nkomo",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0826277342",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Everything was very good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Everything was very good.the staff very helpful"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Everything explained in a manner that I understand "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "The staff is amazing n friendly"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Amazing"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "I wish that those who come far should stat Abit longer"
          }
        ],
        "overallScore": 100,
        "emailAddress": "salomemthimunye13@gmail.com",
        "surname": "Mthimunye",
        "dateCaptured": "2024-06-18T07:12:52.262Z",
        "ward": "General Ward",
        "comment": "No",
        "feedback": "yes",
        "id": "2418-09127",
        "name": "Salome",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0649714703",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Happy patient"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Happy patient"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Happy patient "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Very happy patient "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Add more meat to the meals"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Happy patient "
          }
        ],
        "overallScore": 96.5,
        "emailAddress": "sharlottemolomo@gmail.com",
        "surname": "Molomo",
        "dateCaptured": "2024-09-18T10:02:56.109Z",
        "ward": "Surgical B",
        "comment": "Everything was just fine compared to other hospitals that i have been admitted to.",
        "feedback": "yes",
        "id": "2418-12023",
        "name": "Ipeleng",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0720292177",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Best care at its best "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Doctor Mashaba is the best "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Rooms are tidy"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Food well cooked and delicious "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "The best hospital in south Africa\nThey should add wellness care ward"
          }
        ],
        "overallScore": 100,
        "emailAddress": "hlamalanemufamadi@gmail.com",
        "surname": "Makhubela ",
        "dateCaptured": "2024-08-29T07:39:22.563Z",
        "ward": "General Ward",
        "comment": "Extend the hospitality by adding wellness\nThe best of the staff proved their capabilities ",
        "feedback": "yes",
        "id": "2429-09395",
        "name": "Hlamalane ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0660611480",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Ñono"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "No o"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Keep up the good work "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "No"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "You must cook your food with love not just cook \nNot all of us are happy to eat diabetic kind of food "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Keep up the good work botshilu\n"
          }
        ],
        "overallScore": 58.3,
        "emailAddress": "nono",
        "surname": "Skhosana",
        "dateCaptured": "2024-06-13T10:01:19.460Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2413-12017",
        "name": "Busisiwe",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0764488467",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "maifophehli@gmail.com",
        "surname": "Maifo",
        "dateCaptured": "2024-08-27T13:52:06.144Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2427-15522",
        "name": "Phehli",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0671317750",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "The number of visitors must be at least 2 and give parents more time with their kids during visiting hours."
          }
        ],
        "overallScore": 64.3,
        "emailAddress": "ruthmutombene9@gmail.com",
        "surname": "Mutombene",
        "dateCaptured": "2024-06-12T10:45:36.972Z",
        "ward": "Paeds",
        "comment": "I was in the maternity ward for 3 days maybe you can do like other private hospitals to allow dad's to visit anytime they want to just to bond with their kids as much as the mother does.i would love to have other kids but when I think about that treatment am thinking to opt for other hospitals.please improve on that.",
        "feedback": "yes",
        "id": "2412-12455",
        "name": "Ruth",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0790712553",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Everything went well the way they welcome me i was so happy "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "They just give the medication without explain the side effects"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "she explain just a explain little not  much"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Bathroom very very clean m so happy"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "They no how to do their job the is nice"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Love,kind and most of all their hard workers u will never go wrong"
          }
        ],
        "overallScore": 78.3,
        "emailAddress": "",
        "surname": "Mokone",
        "dateCaptured": "2024-08-27T10:42:03.417Z",
        "ward": "General Ward",
        "comment": "No",
        "feedback": "no",
        "id": "2427-12428",
        "name": "Dolly",
        "reference": "Search engine (e.g., Google or Bing)"
      },
      {
        "cellphonenumber": "0793837441",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "connmkalipi@gmail.com",
        "surname": "Mkalipi",
        "dateCaptured": "2024-07-16T09:45:23.516Z",
        "ward": "Paeds",
        "comment": "Keep giving the good services we appreciate🥰",
        "feedback": "yes",
        "id": "2416-11451",
        "name": "Constance",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0795849654",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 57.4,
        "emailAddress": "fumanitheo994@gmail.com",
        "surname": "Khambane",
        "dateCaptured": "2024-09-17T10:47:12.077Z",
        "ward": "General Ward",
        "comment": "No",
        "feedback": "yes",
        "id": "2417-12472",
        "name": "Theo",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0699995614",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I was very sick but the service that I received it just make me feel at 🏠 a lot 💗💗💗💗"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Satisfied "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Very clean "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good meals"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "keitumetsee@90.com",
        "surname": "Mabungu",
        "dateCaptured": "2024-06-11T08:16:42.304Z",
        "ward": "General Ward",
        "comment": "No",
        "feedback": "yes",
        "id": "2411-10161",
        "name": "Jimmy ",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0718906925",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The nurses were all great "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "The size of juice is too small "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 97.4,
        "emailAddress": "siviwecambalele@gmail.com",
        "surname": "Cambalele ",
        "dateCaptured": "2024-09-18T08:46:05.794Z",
        "ward": "General Ward",
        "comment": "The treatment was excellent from all the staff especially the night shift nurse who was responsible for my ward but unfortunately I forgot her name. It was my first time to be admitted in Hospital and I cannot complain about anything ",
        "feedback": "no",
        "id": "2418-10464",
        "name": "Siviwe ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0614909833",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Courtesy was always applied "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "kgatleditiro@gmail.com",
        "surname": "Kgatle",
        "dateCaptured": "2024-07-19T08:24:22.305Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2419-10248",
        "name": "Ditiro",
        "reference": "Search engine (e.g., Google or Bing)"
      },
      {
        "cellphonenumber": "0815203294",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "We had to wait for discharge to get a bed, but we finally settled in quite nice, very friendly staff of course welcoming "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 80,
        "emailAddress": "nonkosimkize@gmail.com",
        "surname": "Mkize",
        "dateCaptured": "2024-08-28T13:28:33.438Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2428-15284",
        "name": "Nandipa Nonkosi",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0794258534",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 70.4,
        "emailAddress": "onicatshwane@gmail.com",
        "surname": "Tshwane",
        "dateCaptured": "2024-06-18T11:28:18.053Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2418-13285",
        "name": "Mponeng",
        "reference": "Search engine (e.g., Google or Bing)"
      },
      {
        "cellphonenumber": "0630152911",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The best hospital ever"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Always friendly and easy to approach "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Good at evaluating"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 100,
        "emailAddress": "jabulilengomezulu3@gmail.com",
        "surname": "Ngomezulu",
        "dateCaptured": "2024-07-23T06:46:14.687Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2423-08463",
        "name": "Jabulile",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0794105690",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Treatment was excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Awesome "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Everything was exceptionally good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Thank for good service "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "First priority "
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "marthampitso18@gmail.com",
        "surname": "Pitso",
        "dateCaptured": "2024-08-29T07:50:49.792Z",
        "ward": "General Ward",
        "comment": "Everything was excellent ",
        "feedback": "no",
        "id": "2429-09507",
        "name": "Marthap",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0732466186",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Everything was done on acceptable time"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Satisfying "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good hospital "
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "vigi.dlov@gmail.com",
        "surname": "Ndhlovu",
        "dateCaptured": "2024-06-12T09:06:04.395Z",
        "ward": "Paeds",
        "comment": "Can parents with bottle feeding babies have a microwave sterilizer or sterilizing solution for baby bottles coz we only wash bottles for days ",
        "feedback": "yes",
        "id": "2412-11068",
        "name": "Lindiwe. ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0835379090",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The service was extremely poor\n\n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Very good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Satisfied "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Very clean"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "We didn't get the menu but they ask others in the room but they didn't ask him, now they gave him sandwich that he doesn't eat."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "The reception was drastically poor"
          }
        ],
        "overallScore": 64.3,
        "emailAddress": "Lebohang.tlotlo@gmail.com",
        "surname": "Tshukudu",
        "dateCaptured": "2024-06-20T08:06:29.827Z",
        "ward": "Surgical B",
        "comment": "Some receptionist they need refresher causes as per customer service is concern.",
        "feedback": "no",
        "id": "2420-10064",
        "name": "Tlotlo",
        "reference": "Facebook"
      },
      {
        "cellphonenumber": "0797406503",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "radio",
                "score": "5",
                "number": ""
              }
            ],
            "section": "2. How long did the staff take before they attended to you?",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "3.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "3. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "4.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "4. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the cleanliness of the ER unit and bathroom facilities"
              }
            ],
            "section": "5. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. How would you rate your experience at our ER Unit"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.2. How would you rate the duration of the consultation process our ER Unit"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.3. How likely are you to recommend our hospital to your family and friends"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.4. Have you visited Botshilu Private Hospital before"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.5. Did any of our staff members go out of their way to make you feel special during your visit at our ER Unit"
              },
              {
                "answer": "text",
                "score": "No one",
                "number": "6.6. Name of the staff member"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 88.2,
        "emailAddress": "",
        "surname": "Mashaba",
        "dateCaptured": "2024-09-20T07:23:47.875Z",
        "ward": "Emergency Unit (ER)",
        "feedback": "yes",
        "id": "2420-09234",
        "name": "Mackson",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0671924696",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 95.7,
        "emailAddress": "oratilebetty60@gmail.com",
        "surname": "Mogale",
        "dateCaptured": "2024-06-26T13:25:46.744Z",
        "ward": "Maternity",
        "feedback": "yes",
        "id": "2426-15251",
        "name": "Oratile",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0665324609",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "radio",
                "score": "5",
                "number": ""
              }
            ],
            "section": "2. How long did the staff take before they attended to you?",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "3.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "3. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "4.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "4. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the cleanliness of the ER unit and bathroom facilities"
              }
            ],
            "section": "5. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. How would you rate your experience at our ER Unit"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.2. How would you rate the duration of the consultation process our ER Unit"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.3. How likely are you to recommend our hospital to your family and friends"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.4. Have you visited Botshilu Private Hospital before"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.5. Did any of our staff members go out of their way to make you feel special during your visit at our ER Unit"
              },
              {
                "answer": "text",
                "score": "All of them ",
                "number": "6.6. Name of the staff member"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Can not complain everyone was nice and welcoming "
          }
        ],
        "overallScore": 88.2,
        "emailAddress": "",
        "surname": "Baloi",
        "dateCaptured": "2024-09-20T07:35:37.724Z",
        "ward": "Emergency Unit (ER)",
        "feedback": "yes",
        "id": "2420-09356",
        "name": "Kurhula",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0797670011",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 1,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 80,
        "emailAddress": "nthabisengmamakego6@gmail.com",
        "surname": "Shilakwe",
        "dateCaptured": "2024-09-23T12:43:41.198Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2423-14433",
        "name": "Nthabiseng",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0828771379",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "neviumnxu83@gmail.com",
        "surname": "Nxumalo ",
        "dateCaptured": "2024-08-27T10:12:19.516Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2427-12125",
        "name": "Neavium dikeledi ",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0608467854",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Keep up the good work "
          }
        ],
        "overallScore": 100,
        "emailAddress": "",
        "surname": "Petja",
        "dateCaptured": "2024-09-19T09:24:44.452Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2419-11241",
        "name": "Lerato",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0633939141",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Patient feels happy about service \n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Happy with the nursing care"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Patient very excited with the Doctors care he is now able to see"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Room neat and clean "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Food was good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "The service of the hospital was wonderful I will definitely recommend family and friends "
          }
        ],
        "overallScore": 93,
        "emailAddress": "",
        "surname": "Rankapole",
        "dateCaptured": "2024-06-12T07:20:20.070Z",
        "ward": "Surgical B",
        "comment": "The service of the hospital it has been good to me and my wife since COVID-19 started. Keep up the good work 😀😁",
        "feedback": "yes",
        "id": "2412-09203",
        "name": "Matedi",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0722526539",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Everything was perfect "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Everything was excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Everything was fine"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Everything was perfect "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Everything was fine"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Everything was good "
          }
        ],
        "overallScore": 96.5,
        "emailAddress": "roggarsnkosi@gmail.com",
        "surname": "Nkosi",
        "dateCaptured": "2024-06-06T06:45:13.792Z",
        "ward": "General Ward",
        "comment": "Keep up the good work ",
        "feedback": "yes",
        "id": "2406-08454",
        "name": "Roggars",
        "reference": "Facebook"
      },
      {
        "cellphonenumber": "0725546812",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "None"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Well done 👍"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Extremely good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good hospital "
          }
        ],
        "overallScore": 100,
        "emailAddress": "matlalaabraha22@gmail.com",
        "surname": "Matlala ",
        "dateCaptured": "2024-09-07T07:26:08.977Z",
        "ward": "General Ward",
        "comment": "Everything was excellent ",
        "feedback": "yes",
        "id": "2407-09266",
        "name": "Abraham ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0676485578",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I loved everything , very demure "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "I was well taken care of "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 86.1,
        "emailAddress": "mmabasothon@gmail.com",
        "surname": "Ntjana ",
        "dateCaptured": "2024-09-12T08:46:34.762Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2412-10461",
        "name": "Mmabasotho ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0671446241",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 89.6,
        "emailAddress": "dikelediingrid@gmail.com",
        "surname": "Maluleka",
        "dateCaptured": "2024-07-23T12:41:46.006Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2423-14414",
        "name": "Dikeledi",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0796900678",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "I waited for almost 5hours before admission"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "None"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Yes she did. I really love her she's a good doctor (Dr Mathibedi)"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "No comment "
          }
        ],
        "overallScore": 73.9,
        "emailAddress": "Kabelopenelope23@gmail.com",
        "surname": "Chauke",
        "dateCaptured": "2024-05-28T09:14:17.264Z",
        "ward": "Paeds",
        "comment": "No thanks.",
        "feedback": "yes",
        "id": "2428-11145",
        "name": "Kabelo",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0744408840",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Keep up the good work"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "I felt at home"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Very friendly "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Our ward was very clean"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Very satisfied \n"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Please remove visiting hours"
          }
        ],
        "overallScore": 98.3,
        "emailAddress": "tool.mokoka@gmail.com",
        "surname": "Mokoka",
        "dateCaptured": "2024-07-29T13:25:17.583Z",
        "ward": "General Ward",
        "comment": "Very impressed 👍",
        "feedback": "yes",
        "id": "2429-15254",
        "name": "Oarabile",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0814585754",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Staff were friendly enough"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Not much info was given, based on the fact that I'm from the little background of pharmacological aspects. "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "For future purposes, inform patients that Doctors only visit this facility once, as I've waited the entire day and a few more hours, it made me unhappy. A Dr should see and give other Drs access to their patient, if they only do rounds in the morning. The hospital serves as a place of care and nurture, it needs to feel that way as well with a Dr or 2 assigned to a ward in general. "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Keep it up"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 70.4,
        "emailAddress": "",
        "surname": "Mashishi",
        "dateCaptured": "2024-08-29T10:29:01.261Z",
        "ward": "General Ward",
        "feedback": "yes",
        "id": "2429-12296",
        "name": "Zaccheus "
      },
      {
        "cellphonenumber": "0836508000",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Keep them working till pension "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Il keep on coming "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Keep it up"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Keep it up "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Family is fare but yes il do"
          }
        ],
        "overallScore": 100,
        "emailAddress": "",
        "surname": "Mohalw",
        "dateCaptured": "2024-05-31T11:23:17.555Z",
        "ward": "Surgical B",
        "comment": "Keep the staff till they go to pension ",
        "feedback": "yes",
        "id": "2431-13238",
        "name": "Erna",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0827534117",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "mbrakoto1@yahoo.com",
        "surname": "Rakoto",
        "dateCaptured": "2024-08-21T06:46:48.318Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2421-08465",
        "name": "Mapula",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0637998095",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Keep up the good work"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "No comment "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "I wish you continue with your good work..second admission but I am happy with your service "
          }
        ],
        "overallScore": 97.4,
        "emailAddress": "rosemamoloko12.com@gmail.com",
        "surname": "Ntlekoana",
        "dateCaptured": "2024-09-03T10:11:29.644Z",
        "ward": "General Ward",
        "comment": "No",
        "feedback": "yes",
        "id": "2403-12117",
        "name": "Rosemary",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0698125744",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "1.1. How would you rate the friendliness and courtesy of the reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "radio",
                "score": "1",
                "number": ""
              }
            ],
            "section": "2. How long did the staff take before they attended to you?",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "3.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "3.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "3. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "4.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "4. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the cleanliness of the ER unit and bathroom facilities"
              }
            ],
            "section": "5. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. How would you rate your experience at our ER Unit"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.2. How would you rate the duration of the consultation process our ER Unit"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.3. How likely are you to recommend our hospital to your family and friends"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.4. Have you visited Botshilu Private Hospital before"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.5. Did any of our staff members go out of their way to make you feel special during your visit at our ER Unit"
              },
              {
                "answer": "text",
                "score": "Sr Selokelo",
                "number": "6.6. Name of the staff member"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 74.1,
        "emailAddress": "",
        "surname": "Mashishi",
        "dateCaptured": "2024-09-20T07:04:37.029Z",
        "ward": "Emergency Unit (ER)",
        "feedback": "yes",
        "id": "2420-09041",
        "name": "Boikgantsho",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0729504292",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "May good Lord bless them "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Thanks "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good "
          }
        ],
        "overallScore": 100,
        "emailAddress": "na",
        "surname": "Mogotsi",
        "dateCaptured": "2024-09-12T14:49:54.860Z",
        "ward": "General Ward",
        "comment": "None",
        "feedback": "yes",
        "id": "2412-16498",
        "name": "Tinki",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0794105690",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Am happy about nursing \ncare En Matabane\nRn Khoza\nRn Mabina"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "I enjoy my meal"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 97.4,
        "emailAddress": "marthampitso18@gmail.com",
        "surname": "Pitso",
        "dateCaptured": "2024-09-02T15:42:14.458Z",
        "ward": "Surgical A",
        "feedback": "yes",
        "id": "2402-17427",
        "name": "Marthap",
        "reference": "Facebook"
      },
      {
        "cellphonenumber": "0833552826",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The were a family to my child"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "The DR WAS LIKE A MOTHER TO MY CHILD"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 91.3,
        "emailAddress": "hlengiwemaifo@gmail.com",
        "surname": "Maifo",
        "dateCaptured": "2024-08-01T12:12:50.142Z",
        "ward": "Paeds",
        "feedback": "no",
        "id": "2401-14129",
        "name": "Hlengiwe",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0717366980",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Very friendly staff and fast in service"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 77.4,
        "emailAddress": "dimatiidah22@gmail.com",
        "surname": "Dimati",
        "dateCaptured": "2024-07-08T13:24:42.087Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2408-15242",
        "name": "Seipati",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0764751229",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Keep up the good work "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Keep up the excellent work "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Keep up the great work "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Keep up the good work "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Keep up the good work "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Keep up the good work "
          }
        ],
        "overallScore": 96.5,
        "emailAddress": "",
        "surname": "Tlhabye",
        "dateCaptured": "2024-09-11T11:27:33.077Z",
        "ward": "General Ward",
        "comment": "Keep up the good work ",
        "feedback": "yes",
        "id": "2411-13278",
        "name": "Matshidiso",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0637101329",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 88.7,
        "emailAddress": "elwimanamela@gmail.com",
        "surname": "Manamela",
        "dateCaptured": "2024-09-20T10:06:19.282Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2420-12066",
        "name": "Moelelwa ",
        "reference": "Search engine (e.g., Google or Bing)"
      },
      {
        "cellphonenumber": "0608256198",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "It was a very lovely stay please provide headset for patients without "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Excellent "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Less cheese and place on the side to add if more is needed"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93,
        "emailAddress": "dreshne101@gmail.com",
        "surname": "Sibisi",
        "dateCaptured": "2024-05-28T09:43:31.189Z",
        "ward": "Maternity",
        "feedback": "yes",
        "id": "2428-11436",
        "name": "Lesego Dreshne",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0733501573",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 67,
        "emailAddress": "kabelolld@gmail.com",
        "surname": "Rammutla",
        "dateCaptured": "2024-06-23T09:35:25.988Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2423-11359",
        "name": "Ogorogile",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0614651202",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Minimize paperwork"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "buangweb@gmail.com",
        "surname": "Rikhotso",
        "dateCaptured": "2024-08-27T09:44:13.348Z",
        "ward": "Surgical B",
        "comment": "The tv channels need to be upgraded",
        "feedback": "yes",
        "id": "2427-11442",
        "name": "Buang",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0678212405",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Amazing service from all staff and the excellent part is that they connect with patients and treat them like family.i love you an may the good lord keep you safe.ka leboga that's bo love baka u have made this difficult journey look so simple and perfect.to doctor Kalenga you are a Good sent to me an my Daughter we appreciate you sir....God Bless"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Dr Kalenga saved my daughter's life thank you sir"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "It felt like I was home"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "The food was amazing and delicious "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Family and friends will never ever stop hearing me talk about you guys"
          }
        ],
        "overallScore": 100,
        "emailAddress": "abitamasenya@gmail.com",
        "surname": "Masenya",
        "dateCaptured": "2024-09-20T13:51:31.434Z",
        "ward": "Maternity",
        "comment": "I love you and keep up the good work.stay blessed",
        "feedback": "yes",
        "id": "2420-15516",
        "name": "Abita Julia",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0825446465",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 79.1,
        "emailAddress": "manthoalid@gmail.com",
        "surname": "Manthoali",
        "dateCaptured": "2024-09-09T13:59:43.128Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2409-15593",
        "name": "Disemelo",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0723142828",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "radio",
                "score": "5",
                "number": ""
              }
            ],
            "section": "2. How long did the staff take before they attended to you?",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "3.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "3. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "4.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "4. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the cleanliness of the ER unit and bathroom facilities"
              }
            ],
            "section": "5. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "6.1. How would you rate your experience at our ER Unit"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.2. How would you rate the duration of the consultation process our ER Unit"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.3. How likely are you to recommend our hospital to your family and friends"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.4. Have you visited Botshilu Private Hospital before"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.5. Did any of our staff members go out of their way to make you feel special during your visit at our ER Unit"
              },
              {
                "answer": "text",
                "score": "Siyabonga Vilakazi",
                "number": "6.6. Name of the staff member"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 92.9,
        "emailAddress": "",
        "surname": "Masilela",
        "dateCaptured": "2024-09-20T07:12:59.063Z",
        "ward": "Emergency Unit (ER)",
        "feedback": "yes",
        "id": "2420-09122",
        "name": "Christopher",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0679168008",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93.9,
        "emailAddress": "keabetsweitshegetseng29@gmail.com",
        "surname": "Itshegetseng",
        "dateCaptured": "2024-09-08T09:46:22.367Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2408-11463",
        "name": "Keabetswe",
        "reference": "Specialist referral"
      },
      {
        "cellphonenumber": "0822863077",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 93,
        "emailAddress": "jdmogototoane@gmail.com",
        "surname": "Mogototoane",
        "dateCaptured": "2024-09-02T10:33:47.397Z",
        "ward": "Paeds",
        "comment": "No",
        "feedback": "yes",
        "id": "2402-12339",
        "name": "Dimakatso",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0609400327",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 98.3,
        "emailAddress": "mphomakoe2@gmail.com",
        "surname": "Makoe",
        "dateCaptured": "2024-09-01T12:33:19.631Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2401-14338",
        "name": "Sarah",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0796752808",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Everything I was explained to me."
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Yes everything was explained to me "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Frequently "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "I was not happy with the menu "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 2,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 1,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "I wouldn't recommend the hospital to my family members "
          }
        ],
        "overallScore": 73,
        "emailAddress": "",
        "surname": "Baloyi",
        "dateCaptured": "2024-08-07T10:31:30.442Z",
        "ward": "General Ward",
        "comment": "My suggestion is that can visiting hours be extended because I think one hour is too little for someone visiting from far away for example from Johannesburg. Can visiting hours be extended to two hours please.",
        "feedback": "no",
        "id": "2407-12313",
        "name": "Julia ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0763810477",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 99.1,
        "emailAddress": "zanemok1@gmail.com",
        "surname": "Mokwena",
        "dateCaptured": "2024-07-17T12:28:26.886Z",
        "ward": "Paeds",
        "feedback": "yes",
        "id": "2417-14285",
        "name": "Zanele",
        "reference": "Family/Friend/Colleague recommendation"
      },
      {
        "cellphonenumber": "0722677601",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 96.5,
        "emailAddress": "",
        "surname": "Kgolane ",
        "dateCaptured": "2024-09-12T11:34:44.753Z",
        "ward": "Surgical B",
        "comment": "No",
        "feedback": "yes",
        "id": "2412-13345",
        "name": "Kgomotso ",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0789436456",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "0",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 85.2,
        "emailAddress": "koketsoclara06@gmail.com",
        "surname": "Mahlangu",
        "dateCaptured": "2024-06-05T07:39:32.043Z",
        "ward": "ICU",
        "feedback": "no",
        "id": "2405-09393",
        "name": "Clarah",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0729711825",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good"
          }
        ],
        "overallScore": 100,
        "emailAddress": "n/a",
        "surname": "Msiza",
        "dateCaptured": "2024-05-31T13:27:14.938Z",
        "ward": "Surgical B",
        "feedback": "yes",
        "id": "2431-15272",
        "name": "Albertinah",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0829433758",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Security being checked 3 times from main gate main entrance and another security asking same thing at the door of the ward , understand but it's too much"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 4,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Security is too much being checked at the gate and 2 x again"
          }
        ],
        "overallScore": 80,
        "emailAddress": "velimangena@gmail.com",
        "surname": "Mangena",
        "dateCaptured": "2024-08-23T15:09:58.489Z",
        "ward": "Surgical B",
        "feedback": "no",
        "id": "2423-17097",
        "name": "Welheminah",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0729559029",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "Everything was perfectly done"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "Very good "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": "Good"
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 2,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": "For kids pls provide them with pap not other than that "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": "Good "
          }
        ],
        "overallScore": 86.1,
        "emailAddress": "nellynduli99@gmail.com",
        "surname": "Nduli",
        "dateCaptured": "2024-08-28T11:39:13.448Z",
        "ward": "Paeds",
        "comment": "I'm happy The way the operates ",
        "feedback": "yes",
        "id": "2428-13397",
        "name": "Nelly",
        "reference": "GP referral"
      },
      {
        "cellphonenumber": "0797484116",
        "survey": [
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "1.1. How would you rate the friendliness and courtesy of the Reception staff"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.2. Was the waiting time acceptable to you"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "1.3. Were you satisfied with the admission process"
              }
            ],
            "section": "1. Reception and Admission",
            "comment": "The staff was friendly always willing to assist "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "2.1. How would you rate the friendliness and courtesy of the Nursing staff"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "2.2. How was the response time of the Nurses when you pressed the call button"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.3. Were your medical treatment and procedures explained to you adequately"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "2.4. When given medication, did the Nurses explain its purpose and potential side effects to you"
              }
            ],
            "section": "2. Nursing Care",
            "comment": "Nurse Granny she's the best friendly and professional "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "3.1. How would you rate the friendliness and courtesy of your Doctor"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "3.2. How satisfied were you with the level of medical care provided by your Doctor"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "3.3. Did your Doctor sufficiently explain your condition and treatment plan to you"
              }
            ],
            "section": "3. Doctor Care",
            "comment": "The Doctor explained properly "
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 3,
                "number": "4.1. How would you rate the friendliness and courtesy of the Cleaning staff"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.2. How would you rate the cleanliness of the ward and bathroom facilities"
              },
              {
                "answer": "rating",
                "score": 3,
                "number": "4.3. How frequently was your room and bathroom cleaned during your stay"
              }
            ],
            "section": "4. Cleaning",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "5.1. How would you rate the friendliness and courtesy of the Catering staff"
              },
              {
                "answer": "none",
                "score": "",
                "number": "5.2. Please indicate your level of satisfaction on the following questions:"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.1. Menu choice"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.2. Presentation of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.3. Temperature of meals"
              },
              {
                "answer": "rating",
                "score": 4,
                "number": "5.2.4. Taste and quality of meals"
              }
            ],
            "section": "5. Catering",
            "comment": ""
          },
          {
            "question": [
              {
                "answer": "rating",
                "score": 5,
                "number": "6.1. Overall, how would you rate your experience at our hospital"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.2. Were the noise levels within the hospital acceptable to you during the day"
              },
              {
                "answer": "yes/no",
                "score": "5",
                "number": "6.3. Were the noise levels within the hospital acceptable to you during the night?"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.4. How would you rate the accommodation and comfort provided to your visitors"
              },
              {
                "answer": "rating",
                "score": 5,
                "number": "6.5. How likely are you to recommend our hospital to your family and friends"
              }
            ],
            "section": "6. Overall Assessment",
            "comment": ""
          }
        ],
        "overallScore": 90.4,
        "emailAddress": "celesterskosana@gmail.com",
        "surname": "Skosana",
        "dateCaptured": "2024-08-09T10:40:09.177Z",
        "ward": "Paeds",
        "comment": "Ipad would more appropriate so a person can take a  survey sitting down ",
        "feedback": "no",
        "id": "2409-12407",
        "name": "Rose",
        "reference": "GP referral"
      }
    ])
  }

}
